// File: frontend/src/i18n/reputation.js
// Version: 1.0 (2026-10-15)
// Description: Словарь экрана Reputation / GrowthWorkspace (EN/RU),
//              функции перевода и правила множественного числа без внешних зависимостей.
//
// 🔴 ЗДЕСЬ ТОЛЬКО ТЕКСТЫ ИНТЕРФЕЙСА. Фактов фирмы (название, адрес, телефон, имя
//    адвоката, цены, номера Bar) в словаре быть НЕ МОЖЕТ: они приходят данными
//    из API (/reputation/citations/summary, /ofisy/, /branding) и подставляются
//    через переменные `{x}`.
//
// 🔴 Различия, которые словарь и экран обязаны держать:
//    · not_measured («не измерено» / «not measured»)
//      ≠ no_source («источника нет» / «no source»)
//      ≠ zero («0» / «0 обращений» / «0 кликов»).
//    Слить их нельзя: это три разных состояния мира.
//
// Паритет EN/RU и совпадение переменных проверяет `__tests__/reputation-slovar.test.js`.

const EN = {
    // ── Состояния и служебные ────────────────────────────────────────────────
    'rep.state.loading': 'Loading…',
    'rep.state.not_measured': 'not measured',
    'rep.state.not_checked': 'not checked',
    'rep.state.no_source': 'no source',
    'rep.state.source_not_connected': 'source not connected',
    'rep.state.db_error': 'database read error',
    'rep.state.zero': '0',
    'rep.state.dash': '—',

    // ── Вкладки экрана ───────────────────────────────────────────────────────
    'rep.tab.reputation': 'Reputation & Public Trust',
    'rep.tab.marketing': 'Marketing Pulse',
    'rep.tab.citations': 'Citations & Directories',
    'rep.tab.locations': 'Office Profiles',

    // ── Окна времени ─────────────────────────────────────────────────────────
    'rep.timeframe.30d': '30 days',
    'rep.timeframe.90d': '90 days',

    // ── Верхний ряд карточек Reputation & Trust ──────────────────────────────
    'rep.card.nap_index.title': 'NAP Directory Index',
    'rep.card.nap_index.subtitle': 'Weighted consistency across legal directories',
    'rep.card.prescriptions.title': 'Prescriptions to Fix',
    'rep.card.prescriptions.subtitle': 'Total open drift and compliance items',
    'rep.card.ai_citations.title': 'AI Network Citations',
    'rep.card.ai_citations.subtitle': 'Brand mentions in LLM search results',
    'rep.card.offices.title': 'Offices in Canon',
    'rep.card.offices.subtitle': 'Verified operational locations',
    'rep.card.denominator': '{n} of {m}',
    'rep.card.measured_on': 'measured on {date}',
    'rep.card.measured_by': 'via {method}',

    // ── Сводные карточки Marketing Pulse ─────────────────────────────────────
    'rep.pulse.total_inquiries.title': 'Total Inquiries',
    'rep.pulse.total_inquiries.active': 'active inquiries in {timeframe}',
    'rep.pulse.booked_consults.title': 'Booked Consults',
    'rep.pulse.booked_consults.caption': 'managed outside OS',
    'rep.pulse.booked_consults.reason': 'recorded in OS: {zapisi} {na_obrashcheniy} — bookings managed outside OS',
    'rep.pulse.avg_conversion.title': 'Avg Conversion',
    'rep.pulse.avg_conversion.caption': 'statuses not tracked',
    'rep.pulse.avg_conversion.reason': 'statuses not tracked — {n} of {m} “NEW”',
    'rep.pulse.revenue_pipeline.title': 'Revenue Pipeline',
    'rep.pulse.revenue_pipeline.caption': 'no payments in OS',
    'rep.pulse.revenue_pipeline.reason': 'no payments recorded in OS',
    'rep.pulse.organic_reach.title': 'Organic Reach',
    'rep.pulse.conversion_not_measured': 'conversion not measured',

    // ── Неизмеренные величины (причины) ──────────────────────────────────────
    'rep.pulse.unmeasured.pending_gbp_api': 'not measured: Google Business Profile API not connected',
    'rep.pulse.unmeasured.referral_source_not_asked': 'referral source not asked at intake',
    'rep.pulse.unmeasured.gsc_not_connected': 'Search Console not connected',

    // ── Отсечка 90 дней ──────────────────────────────────────────────────────
    'rep.pulse.cutoff.intake_lost_leads_before_date': 'before Aug 27 intake lost inquiries (reviewed Aug 26), data before this date is incomplete',

    // ── Множественное число: Записи (Bookings) ───────────────────────────────
    'rep.plural.zapisi.one': '{n} booking',
    'rep.plural.zapisi.few': '{n} bookings',
    'rep.plural.zapisi.many': '{n} bookings',

    // ── Множественное число: на M обращений (Per M Inquiries) ─────────────────
    'rep.plural.na_obrashcheniy.one': 'per {m} inquiry',
    'rep.plural.na_obrashcheniy.few': 'per {m} inquiries',
    'rep.plural.na_obrashcheniy.many': 'per {m} inquiries',

    // ── Множественное число: Обращения (Inquiries) ───────────────────────────
    'rep.plural.obrashcheniy.one': '{n} inquiry',
    'rep.plural.obrashcheniy.few': '{n} inquiries',
    'rep.plural.obrashcheniy.many': '{n} inquiries',

    // ── Множественное число: Звонки (Calls) ──────────────────────────────────
    'rep.plural.zvonkov.one': '{n} call',
    'rep.plural.zvonkov.few': '{n} calls',
    'rep.plural.zvonkov.many': '{n} calls',

    // ── Множественное число: Сессии (Sessions) ───────────────────────────────
    'rep.plural.sessiy.one': '{n} session',
    'rep.plural.sessiy.few': '{n} sessions',
    'rep.plural.sessiy.many': '{n} sessions',

    // ── Множественное число: Клики (Clicks) ──────────────────────────────────
    'rep.plural.klikov.one': '{n} click',
    'rep.plural.klikov.few': '{n} clicks',
    'rep.plural.klikov.many': '{n} clicks',

    // ── Множественное число: Точки геосетки (Points) ─────────────────────────
    'rep.plural.tochkam.one': 'across {n} point where card was found',
    'rep.plural.tochkam.few': 'across {n} points where card was found',
    'rep.plural.tochkam.many': 'across {n} points where card was found',

    // ── Множественное число: Отзывы (Reviews) ────────────────────────────────
    'rep.plural.otzyvov.one': '{n} review',
    'rep.plural.otzyvov.few': '{n} reviews',
    'rep.plural.otzyvov.many': '{n} reviews',

    // ── Множественное число: Замеры (Scans) ──────────────────────────────────
    'rep.plural.zamerov.one': '{n} scan',
    'rep.plural.zamerov.few': '{n} scans',
    'rep.plural.zamerov.many': '{n} scans',

    // ── Подписи каналов Marketing Pulse ──────────────────────────────────────
    'rep.channel.voice': '{leads} · {calls} in {timeframe}',
    'rep.channel.voice_db_error': '{leads} · not measured: database read error',
    'rep.channel.email': '{leads} in {timeframe}',
    'rep.channel.form': '{leads} in {timeframe}',
    'rep.channel.chat': '{leads} · {sessions} in {timeframe}',
    'rep.channel.chat_db_error': '{leads} · not measured: database read error',
    'rep.channel.calendar': '{leads} in {timeframe}',
    'rep.channel.other': '{leads} in {timeframe}',

    // ── Search Console (Organic Reach) ───────────────────────────────────────
    'rep.gsc.clicks': 'Clicks',
    'rep.gsc.impressions': 'Impressions',
    'rep.gsc.ctr': 'CTR',
    'rep.gsc.position': 'Avg position',
    'rep.gsc.caption.no_source': 'no source',
    'rep.gsc.caption.stale_since': 'not measured since {date}',
    'rep.gsc.caption.no_impressions': 'Search Console data through {date}, no impressions',
    'rep.gsc.caption.data_with_lag': 'Search Console data through {date}, ~{lag} days lag',
    'rep.gsc.stale_badge': 'stale',
    'rep.gsc.curve_not_built': 'Organic reach curve is not built: {detail}',

    // ── Каталоги: Статусы (Citations & Directories) ──────────────────────────
    'rep.citations.status.MATCH': 'Match',
    'rep.citations.status.MINOR_DRIFT': 'Minor drift',
    'rep.citations.status.CRITICAL_DRIFT': 'Critical drift',
    'rep.citations.status.MISSING_LISTING': 'Missing profile',
    'rep.citations.status.NOT_MEASURED': 'Not checked',
    'rep.citations.status.IN_REVIEW': 'In review',
    'rep.citations.status.NOT_COMPARABLE': 'Not comparable',

    // ── Каталоги: Маршруты ───────────────────────────────────────────────────
    'rep.citations.route.both': 'Both locations (Main + Branch)',
    'rep.citations.route.main_only': 'Main location only',
    'rep.citations.route.human_decides': 'Human decides (already listed under branch)',

    // ── Каталоги: Замечания к NAP (Issues) ───────────────────────────────────
    'rep.citations.issue.name_mismatch': 'firm name mismatch',
    'rep.citations.issue.name_is_attorney_not_firm': 'attorney listed instead of firm',
    'rep.citations.issue.name_former_used': 'former name used',
    'rep.citations.issue.phone_mismatch': 'phone number mismatch',
    'rep.citations.issue.street_mismatch': 'street address formatted differently',
    'rep.citations.issue.suite_mismatch': 'suite number mismatch',
    'rep.citations.issue.city_state_zip_mismatch': 'city / state / ZIP mismatch',
    'rep.citations.issue.website_mismatch': 'website mismatch',
    'rep.citations.issue.listed_on_branch_but_route_is_main_only': 'canon marks main-only but listed on branch',
    'rep.citations.issue.submitted_awaiting_platform': 'application submitted, awaiting platform review',

    // ── Каталоги: Следующие действия ─────────────────────────────────────────
    'rep.citations.action.await_platform': 'Await platform review',
    'rep.citations.action.profile_absent': 'Create profile',
    'rep.citations.action.fix_drift': 'Fix drift',
    'rep.citations.action.verify_profile': 'Verify profile',
    'rep.citations.action.monitor': 'Healthy',

    // ── Каталоги: Методы замера ──────────────────────────────────────────────
    'rep.citations.method.api': 'Google Business Information API',
    'rep.citations.method.human_snapshot': 'Human screenshot',
    'rep.citations.method.snapshot_audit': 'Snapshot audit',
    'rep.citations.method.other': 'Audit',
    'rep.citations.method.not_measured': 'not measured',

    // ── Геосетка и профили офисов ────────────────────────────────────────────
    'rep.geogrid.google_maps_via_c2': 'Google Maps via C2 scan',
    'rep.geogrid.divergence_notes': 'Divergence between scan points: majority ({winnerCount} of {pointsFound}) show ★ {winnerRating} ({winnerReviews}). Other values: {otherBreakdown}',
    'rep.geogrid.comparable_diff': '{sign}{diff} pp compared to scan of {date}{lowCov}',
    'rep.geogrid.low_coverage': ' (coverage below threshold)',
    'rep.geogrid.activity_feed': 'Scan History',
    'rep.geogrid.no_scans': 'No scans recorded',

    // ── Заголовки и вкладки GrowthWorkspace ──────────────────────────────────
    'rep.header.badge': 'GROWTH & REPUTATION ENGINE',
    'rep.header.tab_marketing_title': 'Acquisition & conversion pulse',
    'rep.header.tab_reputation_title': 'Public trust — no measurement source',
    'rep.header.tab_citations_title': 'Verified legal citation network',
    'rep.header.tab_locations_title': 'Office locations & truth profiles',
    'rep.header.tab_marketing_desc': 'Demand metrics across Sofia Voice AI, Google Maps, client referrals and website chat.',
    'rep.header.tab_reputation_desc': 'Reputation signals here are NOT measured: these cards have no live source.',
    'rep.header.tab_citations_desc': 'Accounts, addresses and bar admissions. Binding address is sourced from office holder.',
    'rep.header.tab_locations_desc': 'Manage office branches, launch 10-Agent Squad battles, configure NAP and API workers.',
    'rep.tabs.locations': 'Office Profiles ({n})',

    // ── Кнопки шапки GrowthWorkspace ─────────────────────────────────────────
    'rep.action.agent_battle': 'Agent Battle Arena',
    'rep.action.source_of_truth': 'Source of Truth',
    'rep.action.analytics': 'Analytics',
    'rep.action.run_full_audit': 'Run Full Audit',

    // ── Верхний ряд карточек Reputation & Trust ──────────────────────────────
    'rep.card.open_issues.title': 'Open Issues',
    'rep.card.open_issues.prescriptions': 'Prescriptions in report: {n}',
    'rep.card.open_issues.critical': 'critical: {n}',
    'rep.card.open_issues.normal': 'normal: {n}',
    'rep.card.open_issues.no_prescriptions': 'no prescriptions in report',
    'rep.card.nap_citations.title': 'NAP & AI Citations',
    'rep.card.nap_citations.no_nap': 'NAP not in report',
    'rep.card.nap_citations.canon_unavailable': 'canon not available — NAP measurement not run; neither percentage nor denominator shown',
    'rep.card.nap_citations.measured_den': 'measured {n} of {m}',
    'rep.card.nap_citations.weighted_index': 'weighted index (directory weights), not average',
    'rep.card.nap_citations.ai_citations': 'AI citations: {our} of {total}',
    'rep.card.nap_citations.llm_not_polled': 'LLMs were not queried',
    'rep.card.nap_citations.no_domain': 'firm domain unreadable — nothing to compare',
    'rep.card.nap_citations.express_warning': 'express run: 1 repeat per prompt instead of 3 — lower accuracy',
    'rep.card.nap_citations.btn_launch': 'Launch scan in audit window',
    'rep.card.entity_status.title': 'Public Entity Status',
    'rep.card.entity_status.canon_unavailable': 'canon not available',
    'rep.card.entity_status.canon_locations': 'canon locations',
    'rep.card.entity_status.directories_measured': 'directories measured: {n} of {m}',
    'rep.card.entity_status.statement_what_measured': 'statement of WHAT was measured, not a verification rating',
    'rep.card.nap_baseline.title': 'NAP Baseline',
    'rep.card.nap_baseline.no_index': 'no NAP index in report: ',
    'rep.card.nap_baseline.badge': 'baseline',
    'rep.card.nap_baseline.recorded_at': 'baseline recorded {date}; trend chart requires three consecutive scans',
    'rep.card.nap_baseline.unnamed_moment': 'time not stated',
    'rep.card.nap_baseline.explanation': 'weighted NAP index, not “health”; a single point is not a trend',

    // ── Графики и пульс маркетинга ───────────────────────────────────────────
    'rep.pulse.title': 'Acquisition & Conversion',
    'rep.pulse.subtitle': 'Demand and conversion trend',
    'rep.pulse.tracking_desc': 'Tracking high-intent calls, bookings, and organic lead acquisition.',
    'rep.pulse.metric.leads': 'Leads',
    'rep.pulse.metric.booked': 'Booked calls',
    'rep.pulse.metric.organic': 'Organic reach',
    'rep.pulse.legend.total_inquiries': 'Total Inquiries (Leads)',
    'rep.pulse.legend.booked': 'Booked Consultations',
    'rep.pulse.legend.organic': 'Organic Search Reach',
    'rep.pulse.active_leads_data': 'based on OS database (active leads)',
    'rep.pulse.loading_leads': 'Loading inquiry data from OS database...',
    'rep.pulse.error_leads': 'Failed to get inquiry data: {error}',
    'rep.pulse.db_error_detail': 'not measured: database read error',
    'rep.pulse.no_inquiries_in_window': 'No inquiries recorded in the selected window.',
    'rep.pulse.booked_curve_not_built': 'Booked consultations curve is not built: {reason}.',
    'rep.pulse.loading_gsc': 'Loading Search Console data...',
    'rep.pulse.stale_slice': 'data slice update required',
    'rep.pulse.other_channels_title': 'Other acquisition channels and measurement status',
    'rep.pulse.channel_inbound_calls': 'inbound calls: {n}',
    'rep.pulse.channel_chat_sessions': 'chat sessions: {n}',
    'rep.pulse.cutoff_prefix': 'Cutoff {date}:',

    // ── Рабочий список каталогов (CitationsWorklist) ──────────────────────────
    'rep.citations.coverage.title': 'Scan coverage',
    'rep.citations.measured_denominator': 'Measured: {n} of {m}',
    'rep.citations.last_scan': 'Last scan: {date}',
    'rep.citations.scan_never_run': 'never run',
    'rep.citations.method_human_screenshot': '(human, screenshots)',
    'rep.citations.consistency_index': 'Consistency index',
    'rep.citations.index_score': 'Index: {score}%',
    'rep.citations.no_network_scan': 'no network scan — metric not measured',
    'rep.citations.weighted_explanation': 'weighted by directory weights; denominator includes only measured — {n} of {m}',
    'rep.citations.active_canons': 'Active firm canons',
    'rep.citations.role_main': 'main, address visible',
    'rep.citations.role_branch': 'branch, SAB',
    'rep.citations.street_hidden': ', street hidden',
    'rep.citations.source': 'Source: {source}',
    'rep.citations.canon_not_set': 'canon not specified',
    'rep.citations.never_publish.title': 'Never publish to public directories',
    'rep.citations.never_publish.desc': 'Bot service line and residential branch fragments. Prohibition is verified when populating profiles.',
    'rep.citations.table.title': 'Tier-1 directories & search super-donors',
    'rep.citations.table.desc': 'Platforms generating 100% citations in LLM search. Fill profiles according to canon route.',
    'rep.citations.btn.prepare_packet': 'Prepare fix packet',
    'rep.citations.btn.collapse_packet': 'Collapse fix packet',
    'rep.citations.empty_directories': 'Data received, no directories in response — nothing to fix.',
    'rep.citations.warnings_parser': 'Warnings from scan table parser:',
    'rep.citations.th.directory': 'Directory',
    'rep.citations.th.route': 'Submitted NAP',
    'rep.citations.th.status': 'Status',
    'rep.citations.th.score_weight': 'Score · weight',
    'rep.citations.th.action': 'Action',
    'rep.citations.super_donor': 'Super-donor',
    'rep.citations.scan': 'Scan: {date}',
    'rep.citations.history': 'History: ',
    'rep.citations.human_decides_desc': 'Requires human decision: automated switching prohibited by canon',
    'rep.citations.not_comparable_desc': 'observation exists, nothing to compare against',
    'rep.citations.observed_prefix': 'scan: {address}',
    'rep.citations.unobserved': 'not visible in screenshot: {items}',
    'rep.citations.weight': 'weight {weight}',
    'rep.citations.not_in_calc': 'not in calculation',
    'rep.citations.action.open_profile': 'Open profile',
    'rep.citations.action.create_profile': 'Create profile',
    'rep.citations.no_full_url': 'full URL not recorded in scan',
    'rep.citations.slug': 'slug: {slug}',
    'rep.citations.error.title': 'Failed to read directory data',
    'rep.citations.error.desc': 'Instrument failure, not “no violations”: flag — {flag}',
    'rep.citations.error.retry': 'Retry request',
    'rep.citations.loading': 'Loading directory worklist and canon…',
    'rep.citations.canon_not_configured': 'Canon not configured',
    'rep.citations.canon_not_configured_desc': 'Citation canon is not yet configured for this practice.',

    // ── Единая подпись замера (PodpisZamera) ──────────────────────────────────
    'rep.podpis.audit_recorded': 'Audit recorded:',
    'rep.podpis.invalid_date': '(timestamp could not be formatted: shown as-is)',
    'rep.podpis.method': 'method: {method}',
    'rep.podpis.method_human_screenshots': 'human screenshots, verified',
    'rep.podpis.method_not_stated': 'measurement method not stated',
    'rep.podpis.timestamp_not_stated': 'timestamp not stated',
    'rep.podpis.cache_age_title': 'exact value: {seconds} s; computed by server',
    'rep.podpis.cache_updated_ago': 'record updated ~{age} ago',
    'rep.podpis.in_process_only': 'response assembled from process memory (storage: in_process_only)',
    'rep.podpis.express_run': 'express run: 1 repeat per prompt instead of 3 — lower accuracy',
    'rep.podpis.time_s': '{n} s',
    'rep.podpis.time_m': '{n} min',
    'rep.podpis.time_h': '{n} h',
    'rep.podpis.time_d': '{n} d',

    // ── Полоса шкалы (Polosa) ────────────────────────────────────────────────
    'rep.bar.no_value': 'no value in response',
    'rep.bar.no_denominator': 'no ratio: denominator is zero — nothing to divide by',
    'rep.bar.measured_zero': 'measured zero',
    'rep.bar.over_100': 'bar capped at 100: value in response exceeds scale (shown as-is)',
    'rep.bar.under_0': 'bar at zero: value in response is below scale (shown as-is)',

    // ── Запасные тексты и причины (Fallbacks) ─────────────────────────────────
    'rep.fallback.no_source_long': 'No source: this metric is not measured — no API endpoint exists to provide it.',
    'rep.fallback.db_read_error': 'database read error',
    'rep.fallback.reason_not_stated': 'reason not stated',
    'rep.fallback.no_profiles': 'No profiles: nothing to open. Profile list is supplied via profiles prop.',
    'rep.fallback.profile_not_linked': 'Profile not linked',

    // ── Названия каналов (Channel Titles) ─────────────────────────────────────
    'rep.channel_title.voice': 'Voice AI (Sofia)',
    'rep.channel_title.email': 'Email (Sofia AI)',
    'rep.channel_title.form': 'Website Form',
    'rep.channel_title.chat': 'Website Chat (Sofia)',
    'rep.channel_title.maps': 'Google Maps',
    'rep.channel_title.referrals': 'Referrals & Attorney Network',
    'rep.channel_title.calendar': 'Widget Calendar',
    'rep.channel_title.other': 'Other',
    // Каналы ELN (data/features/eln.json → marketing_channels[].id)
    'rep.channel_title.form_contact': 'Website Form',
    'rep.channel_title.chat_widget': 'Chat Widget',
    'rep.channel_title.widget_calendar': 'Widget Calendar',
    'rep.channel_title.web_chat': 'Website Chat (Sofia)',

    // ── Стратегический план GMB (Strategic Plan) ─────────────────────────────
    'rep.plan.title': 'Priority: local visibility {office} · “{keyword}”',
    'rep.plan.impact_not_evaluated': 'Impact not evaluated',
    'rep.plan.hypothesis_badge': '[Hypothesis, unverified]',
    'rep.plan.hypothesis_desc': 'Visibility drop outside the central sector of the office may be due to competitor density and lack of local citations.',
    'rep.plan.office_query': 'Office & query:',
    'rep.plan.grid_positions': 'Grid positions:',
    'rep.plan.top3_count': 'in top-3: {n} of {m} ({pct}%)',
    'rep.plan.top3_share': 'top-3 share: {pct}%',
    'rep.plan.top10_count': 'in top-10: {n} of {m} ({pct}%)',
    'rep.plan.top10_share': 'top-10 share: {pct}%',
    'rep.plan.top10_not_measured': 'in top-10: not measured',
    'rep.plan.coverage_label': 'SERP coverage:',
    'rep.plan.found_in_points': 'found in {found} of {total} points',
    'rep.plan.found_simple': 'found in {found} points',
    'rep.plan.not_found_points': '(not found in {n})',
    'rep.plan.leaders_label': 'SERP leaders:',
    'rep.plan.recommendation': 'Recommendation: targeted boost of local citations in sectors with rank >3',
    'rep.plan.next_scan': 'Next scan: per C2 schedule, every 7 days',
    'rep.plan.no_scans': 'No verified geogrid scans yet. Strategic plan is formed dynamically after first C2 scan cycle.',

    // ── Присутствие в справочниках (Directory Presence) ──────────────────────
    'rep.directory_presence.title': 'Active Directory Authority',
    'rep.directory_presence.desc': 'Directory entries are built from office holder canon — one per office, based on canon data',
    'rep.directory_presence.no_check': 'verification not performed',
    'rep.directory_presence.unverified': 'listing status not verified; no confirmed listing link in canon',

    // ── Распределение сигналов (Signal Distribution) ─────────────────────────
    'rep.signals.title': 'Signal Distribution',

    // ── Журнал аудитов и замеров (Audit & Scan History) ───────────────────────
    'rep.audit_history.title': 'Audit & Scan History',
    'rep.audit_history.desc': 'Log of verified geogrid scans by C2 bot',
    'rep.audit_history.scan_time': 'Scan time:',
    'rep.audit_history.queries_count': 'Queries ({n}):',
    'rep.audit_history.queries_list': 'Query list:',
    'rep.audit_history.source_os': 'list: OS',
    'rep.audit_history.source_config': 'list: C2 config',
    'rep.audit_history.source_fallback_reason': 'list: fallback ({reason})',
    'rep.audit_history.source_fallback': 'list: fallback',
    'rep.audit_history.source_not_specified': 'list: not specified',
    'rep.audit_history.google_rating': 'Google Rating:',
    'rep.audit_history.grid': 'Grid: {coverage}',
    'rep.audit_history.schedule': 'Schedule: per C2 schedule, every 7 days',
    'rep.audit_history.empty': 'No verified scans in local storage yet. Scans run on C2 schedule, once every 7 days.',
    'rep.audit_history.status_success': 'Success',

    // ── Профили офисов и канонические привязки ────────────────────────────────
    'rep.office.header_canon_agents': 'OFFICE CANON & 10 AI AGENTS',
    'rep.office.header_profiles_title': 'Office Profiles & Canon Anchors',
    'rep.office.header_profiles_desc': 'Launch agent battles, manage NAP credentials, and verify geogrid scans',
    'rep.office.strategic_plan_header': 'Strategic Action Plan',
    'rep.office.priority_recommendation': 'Priority Recommendation',
    'rep.office.truth_main_title': 'Source of truth for main profile',
    'rep.office.main_canon': 'Main canon office.',
    'rep.office.main_canon_badge': 'Main canon office',
    'rep.office.branch_canon': 'Canon branch.',
    'rep.office.branch_badge': 'Branch',
    'rep.office.hours_notice': 'Intake hours and schedule are not shown on this screen: they are held in the office canon, and a second copy is not created here.',
    'rep.office.unlinked_profile_notice': 'Profile not linked: navigation to GMB console, arena, and analytics is disabled.',
    'rep.office.step_no_profile': 'Profile not created in system',
    'rep.office.btn_create_profile': 'Create profile from office canon',
    'rep.office.creating_profile': 'Creating profile...',
    'rep.office.step_no_google': 'Google not connected',
    'rep.office.btn_connect_google': 'Connect Google',
    'rep.office.step_no_place_id': 'Listing not linked (place_id missing in canon)',
    'rep.office.create_profile_error': 'Failed to create profile: {error}',
    'rep.office.create_profile_success': 'Office profile created successfully',
    'rep.office.connect_google_error': 'Failed to connect Google: {error}',
    'rep.office.truth_source_title': 'Office source of truth',
    'rep.office.arena_title': 'Arena',
    'rep.office.analytics_title': 'Analytics Command',
    'rep.office.gmb_console_title': 'Open GMB AI Command Center',
    'rep.office.aria_label': 'Office {name}',
    'rep.office.selected': 'selected',
    'rep.office.visibility_not_measured': 'Visibility not measured',

    // ── Состояние держателя офисов (SostoyanieOfisov) ─────────────────────────
    'rep.sostoyanie_ofisov.loading': 'Offices not yet received from holder: status unknown.',
    'rep.sostoyanie_ofisov.failed': 'Failed to read offices: {error}. This does NOT mean there are no offices — it means canon could not be read, and there is nothing to display here.',
    'rep.sostoyanie_ofisov.empty': 'Tenant has no offices: holder returned an empty list.',

    // ── Множественное число: Показы (Impressions) ────────────────────────────
    'rep.plural.pokazov.one': '{n} impression',
    'rep.plural.pokazov.few': '{n} impressions',
    'rep.plural.pokazov.many': '{n} impressions',

    // ── Маркетинг пульс тултипы и метрики ────────────────────────────────────
    'rep.pulse.tooltip_leads': '{date}: {leads}',
    'rep.pulse.tooltip_gsc': '{date}: {clicks}, {impressions}, CTR {ctr}%, pos. {position}',
    'rep.pulse.pos_prefix': 'pos.',

    // ── Геосетка UI (OfficeVisibilityBlock) ──────────────────────────────────
    'rep.geogrid_ui.service': 'Geogrid service:',
    'rep.geogrid_ui.title': 'Google Maps Visibility',
    'rep.geogrid_ui.configure_queries': 'Configure queries',
    'rep.geogrid_ui.configure_queries_title': 'Configure search queries for this office',
    'rep.geogrid_ui.not_measured': 'Not measured',
    'rep.geogrid_ui.top3_share': 'Share in top-3',
    'rep.geogrid_ui.top10_share': 'Share in top-10',
    'rep.geogrid_ui.scan_never_run': 'Geogrid visibility scan for this location has not been performed yet. It will appear after the first scan by C2 bot.',
    'rep.geogrid_ui.scan_heading': 'Google Maps Visibility',
    'rep.geogrid_ui.scan_title': 'Scan completion time in office timezone: {date}',
    'rep.geogrid_ui.scan_badge': 'Scan {date}',
    'rep.geogrid_ui.rating_unmeasured': 'Google Maps Rating: not measured',
    'rep.geogrid_ui.will_measure_next': 'will be measured on next scan',
    'rep.geogrid_ui.query_label': 'Query:',
    'rep.geogrid_ui.choose_query': 'Choose query',
    'rep.geogrid_ui.search_placeholder': 'Search queries...',
    'rep.geogrid_ui.not_measured_yet_group': 'Not measured yet · will appear after scan',
    'rep.geogrid_ui.nothing_found': 'Nothing found',
    'rep.geogrid_ui.query_unmeasured_notice': 'This query was added in settings and has not been scanned yet. It will appear after next C2 scan.',
    'rep.geogrid_ui.grid_title': 'Geogrid {rows}×{cols} ({size}×{size} km · step ~{step} km · N ↑)',
    'rep.geogrid_ui.office_pin': 'Office',
    'rep.geogrid_ui.north_up': 'N ↑',
    'rep.geogrid_ui.north_up_title': 'Orientation: North is up',
    'rep.geogrid_ui.in_top_3': 'in top-3: {val}',
    'rep.geogrid_ui.found_in_top_20': 'found in top-20: {val}',
    'rep.geogrid_ui.in_top_10': 'in top-10: {val}',
    'rep.geogrid_ui.radius_reach': 'radius reach: {radius} km',
    'rep.geogrid_ui.radius_unmeasured': 'reach: not measured',
    'rep.geogrid_ui.scan_coverage': 'Scan coverage:',
    'rep.geogrid_ui.quality_gate_failed': 'Scan incomplete: 95% quality threshold not reached (coverage {coverage})',
    'rep.geogrid_ui.search_leaders': 'Leaders in local search',
    'rep.geogrid_ui.our_office': '(our office)',
    'rep.geogrid_ui.competitors_not_found': 'No competitors found in top-3',
    'rep.geogrid_ui.disclaimer_title': 'What these numbers do not prove',
    'rep.geogrid_ui.disclaimer_1_title': 'Do not prove calls and inquiries:',
    'rep.geogrid_ui.disclaimer_1_text': 'a high search rank yields impressions, but does not guarantee calls or consultations.',
    'rep.geogrid_ui.disclaimer_2_title': 'Do not account for personalization:',
    'rep.geogrid_ui.disclaimer_2_text': 'real user search results depend on their search history, device, and exact GPS coordinates.',
    'rep.geogrid_ui.disclaimer_3_title': 'Reflect an instantaneous snapshot:',
    'rep.geogrid_ui.disclaimer_3_text': 'ranks are recorded at the moment of scan within a radius around the office coordinates, not forever.',

    // ── Модальные окна (rep.modal.*) ──────────────────────────────────────────
    // FullAuditModal
    'rep.modal.audit.title': 'Full Reputation Audit',
    'rep.modal.audit.subtitle': 'Progress is reported from server events: bar moves when a stage completes, and not otherwise.',
    'rep.modal.audit.close': 'Close',
    'rep.modal.audit.btn_running': 'Audit in progress',
    'rep.modal.audit.btn_run': 'Run audit',
    'rep.modal.audit.disclaimer': 'This run uses paid external resources: queries paid Ahrefs endpoints and queries AI neural networks — one provider per configured key. The exact number of calls and units consumed is not known in advance: prompt count and retries are set on the server, and unit consumption was not measured.',
    'rep.modal.audit.progress': 'Progress',
    'rep.modal.audit.stage.nap': 'NAP directory consistency',
    'rep.modal.audit.stage.ahrefs': 'Domain metrics (Ahrefs)',
    'rep.modal.audit.stage.geo': 'AI search query',
    'rep.modal.audit.stage.prescriptions': 'Action prescriptions',
    'rep.modal.audit.ishod.pending': 'pending',
    'rep.modal.audit.ishod.running': 'running',
    'rep.modal.audit.ishod.ok': 'ready',
    'rep.modal.audit.ishod.not_configured': 'not queried',
    'rep.modal.audit.ishod.failed': 'failed',
    'rep.modal.audit.ishod.not_measured': 'not measured',
    'rep.modal.audit.nap_not_measured': 'Not measured — NAP canon is missing',
    'rep.modal.audit.btn_run_again': 'Run again',
    'rep.modal.audit.reason.missing_key': 'API key is missing ({key})',
    'rep.modal.audit.reason.missing_model': 'Model is not configured ({model_var})',
    'rep.modal.audit.reason.domain_not_set': 'Company domain is not configured',
    'rep.modal.audit.reason.empty_response': 'Received empty response from API',
    'rep.modal.audit.reason.prompts_missing': 'Prompt configuration file is missing for this tenant',
    'rep.modal.audit.reason.nap_canon_missing': 'NAP canon file is missing',
    'rep.modal.audit.reason.nap_canon_invalid': 'NAP canon is invalid or incomplete',
    'rep.modal.audit.reason.none_configured': 'No search providers are configured',
    'rep.modal.audit.reason.all_failed': 'All search providers failed to respond',
    'rep.modal.audit.reason.no_nap_summary': 'NAP audit not measured — prescriptions unavailable',
    'rep.modal.audit.reason.run_not_found': 'Audit run not found in memory (runs reset on restart)',
    'rep.modal.audit.reason.provider_error': 'Provider error ({error})',
    'rep.modal.audit.reason.unexpected_error': 'Unexpected error ({error})',
    'rep.modal.audit.directories': 'Directories',
    'rep.modal.audit.nap_summary': 'Measured {measured} of {total} · index {index}',
    'rep.modal.audit.nap_method': 'measurement method: human screenshot · {date}',
    'rep.modal.audit.domain_metrics': 'Domain Metrics',
    'rep.modal.audit.ahrefs_missing_key': 'API Key missing — {detail}. No values displayed: not measured.',
    'rep.modal.audit.hidden_nested': 'hidden nested fields: {count}',
    'rep.modal.audit.measured_by': 'measured by: {source}',
    'rep.modal.audit.target_domain': 'target domain: {target}',
    'rep.modal.audit.measured_at': 'measured at: {date}',
    'rep.modal.audit.snapshot_for': 'snapshot for {date}',
    'rep.modal.audit.clock': 'clock',
    'rep.modal.audit.no_country_filter': 'country filter was not applied: numbers not country-specific',
    'rep.modal.audit.country_filter': 'country filter: {country}',
    'rep.modal.audit.no_values': 'no values',
    'rep.modal.audit.neural_networks': 'Neural Networks',
    'rep.modal.audit.express_mode': 'express run: one iteration per prompt instead of three — precision is lower, do not compare directly with full run',
    'rep.modal.audit.geo_not_queried': 'not queried — {detail}',
    'rep.modal.audit.geo_stats': 'prompts queried: {done} · firm mentions: {our} of {total} citations · domain unknown: {unknown}',
    'rep.modal.audit.geo_failed': 'failed: {detail}',
    'rep.modal.audit.geo_total': 'Total: firm mentions: {our} of {total} citations (domain unknown: {unknown})',
    'rep.modal.audit.geo_total_desc': 'firm domain links among all citations collected across queried providers',
    'rep.modal.audit.geo_no_summary': 'neural networks were not queried — no summary',
    'rep.modal.audit.what_to_fix': 'What to fix ({count})',
    'rep.modal.audit.open_link': 'Open',
    'rep.modal.audit.storage_disclaimer': 'Report is stored in process memory and will not survive a backend restart (storage: in_process_only).',
    'rep.modal.audit.err_not_started': 'Audit failed to start',
    'rep.modal.audit.err_no_task_id': 'Server did not return a run task ID',
    'rep.modal.audit.err_status_failed': 'Status check failed',

    // GeogridKeywordsModal
    'rep.modal.geogrid.title': 'Configure Search Queries',
    'rep.modal.geogrid.branch': 'Branch: {name}',
    'rep.modal.geogrid.key': 'Key: {kluch}',
    'rep.modal.geogrid.close': 'Close',
    'rep.modal.geogrid.loading': 'Loading keyword settings...',
    'rep.modal.geogrid.source_label': 'Query source:',
    'rep.modal.geogrid.source_os': 'Set in OS',
    'rep.modal.geogrid.source_c2': 'Canonical C2 config',
    'rep.modal.geogrid.author': 'Author: {user}',
    'rep.modal.geogrid.budget_title': 'Scan volume (49 points / query)',
    'rep.modal.geogrid.queries_count': '{count} / {max} queries',
    'rep.modal.geogrid.limit_not_reported': 'Limit not reported',
    'rep.modal.geogrid.single_scan': 'Single scan',
    'rep.modal.geogrid.points_calc': 'queries: {count} × 49 points',
    'rep.modal.geogrid.points_value': 'points: {points}',
    'rep.modal.geogrid.per_month': 'Per month (4 cycles)',
    'rep.modal.geogrid.weekly_scan': 'weekly scan',
    'rep.modal.geogrid.abort_title': 'Previous scan aborted: {reason}',
    'rep.modal.geogrid.abort_desc': 'Planned {planned} requests with budget limit {limit}. Reduce query count to {max} and save settings.',
    'rep.modal.geogrid.query_list_title': 'Query list for scan:',
    'rep.modal.geogrid.min_max': 'min 1, max {max}',
    'rep.modal.geogrid.unknown_list': 'Current list unknown — C2 has not reported yet',
    'rep.modal.geogrid.empty_list': 'List is empty. Add queries below to configure custom list.',
    'rep.modal.geogrid.delete_kw': 'Delete {kw}',
    'rep.modal.geogrid.delete_query': 'Delete query',
    'rep.modal.geogrid.add_query_title': 'Add new query:',
    'rep.modal.geogrid.placeholder': 'Example: probate lawyer manhattan',
    'rep.modal.geogrid.btn_add': 'Add',
    'rep.modal.geogrid.schedule_title': 'How changes are applied:',
    'rep.modal.geogrid.schedule_desc': 'The saved list is synced to C2. The C2 bot will run scans for new queries during the next weekly scan (7 days after the previous one, at 07:15 ET) or upon manual trigger. On the visibility dashboard, new queries appear immediately after the scan completes.',
    'rep.modal.geogrid.btn_saving': 'Saving...',
    'rep.modal.geogrid.btn_save': 'Save in OS',
    'rep.modal.geogrid.err_load': 'Failed to load settings',
    'rep.modal.geogrid.err_save': 'Failed to save settings',
    'rep.modal.geogrid.err_limit_not_reported': 'Query limit not reported by C2 bot',
    'rep.modal.geogrid.err_min_chars': 'Query must be at least 2 characters',
    'rep.modal.geogrid.err_max_chars': 'Query must be at most 200 characters',
    'rep.modal.geogrid.err_max_queries': 'Maximum {max} queries per office (C2 budget limit)',
    'rep.modal.geogrid.err_duplicate': 'This query is already in the list',
    'rep.modal.geogrid.err_at_least_one': 'Office must have at least one search query',
    'rep.modal.geogrid.err_save_blocked': 'Query limit not reported by C2 bot. Saving blocked.',
    'rep.modal.geogrid.err_add_before_save': 'Add at least one search query before saving',
    'rep.modal.geogrid.err_max_allowed': 'Maximum {max} queries allowed for this office',
    'rep.modal.geogrid.success_saved': 'Settings saved in OS',

    // CitationsFixPacket
    'rep.modal.packet.title': 'Fix Packet by Directory',
    'rep.modal.packet.empty': 'Data received, no prescriptions found — nothing to fix. Zero cards does not mean “everything is correct on platforms”: the packet shows only what the audit found, and what the audit did not observe is not listed here.',
    'rep.modal.packet.stats': 'Directories in packet: {ploshchadki} · prescriptions in packet: {vPakete} · outside packet: {vnePaketa} · require human decision: {trebuyutResheniya}',
    'rep.modal.packet.canon_no_value': ' · canon provides no value: {count}',
    'rep.modal.packet.no_write_disclaimer': '🔴 No changes to external directories are made from here: there are no write APIs to any of them. Each step must be performed manually in the directory interface.',
    'rep.modal.packet.ordering_disclaimer': 'Directory order is as returned by the API: starting with primary source directories where aggregators crawl data. This is an ASSUMPTION about third-party system behavior from external audit, not a direct measurement.',
    'rep.modal.packet.prescriptions_count': 'prescriptions: {count}',
    'rep.modal.packet.field': 'Field: {field}',
    'rep.modal.packet.source': ' · source: {role}',
    'rep.modal.packet.btn_copy': 'Copy',
    'rep.modal.packet.btn_copied': 'copied',
    'rep.modal.packet.btn_copy_err': 'failed to copy: {err}',
    'rep.modal.packet.clipboard_unavailable': 'clipboard unavailable in this environment',
    'rep.modal.packet.remove_title': 'Remove from profile',
    'rep.modal.packet.remove_disclaimer': 'There is no value to paste here by definition: this is a publication prohibition, not a replacement of one text with another.',
    'rep.modal.packet.decision_title': 'Human decision required — no ready value',
    'rep.modal.packet.consequence': 'Consequence: {text}',
    'rep.modal.packet.street_sab': ' · street address not published (SAB) — address for relocation cannot be taken from here',
    'rep.modal.packet.canon_no_options': 'Canon does not enumerate options — what to substitute is decided by human.',
    'rep.modal.packet.decision_disclaimer': 'Selection is made directly prior to saving the directory form. Rollbacks on third-party platforms are handled via support, not edits.',
    'rep.modal.packet.unknown_title': 'Canon provides no value — pause and consult',
    'rep.modal.packet.unknown_disclaimer': 'Plausible fabricated value is worse than an empty field: emptiness is visible, fabrication is not.',
    'rep.modal.packet.outside_title': 'Outside packet — no action needed',
    'rep.modal.packet.await_disclaimer': ' — resubmission while application is pending is harmful',
    'rep.modal.packet.fields.name': 'Firm Name',
    'rep.modal.packet.fields.address': 'Address',
    'rep.modal.packet.fields.street': 'Street',
    'rep.modal.packet.fields.suite': 'Suite / Unit',
    'rep.modal.packet.fields.city_state_zip': 'City, State, Zip',
    'rep.modal.packet.fields.phone': 'Phone',
    'rep.modal.packet.fields.website': 'Website',
    'rep.modal.packet.fields.attorney': 'Attorney',
    'rep.modal.packet.fields.bar_number': 'Bar number',
    'rep.modal.packet.roles.MAIN': 'main office',
    'rep.modal.packet.roles.BRANCH': 'branch',
    'rep.modal.packet.variants.city_only.title': 'Keep city only (privacy)',
    'rep.modal.packet.variants.city_only.desc': 'building address is not on platform; canon permits city-level for text directories',
    'rep.modal.packet.variants.street_parity.title': 'Specify street without suite (NAP parity)',
    'rep.modal.packet.variants.street_parity.desc': 'this address is already visible in Google listing, bar registry and directories; consistent full address boosts profile weight',
    'rep.modal.packet.variants.keep_branch.title': 'Keep branch, removing only restricted snippet',
    'rep.modal.packet.variants.keep_branch.desc': 'continuity with profiles and reviews tied to the branch',
    'rep.modal.packet.variants.move_main.title': 'Move to canon main office',
    'rep.modal.packet.variants.move_main.desc': 'bar registry points to main physical office; will require moving remaining single-location listings there as well',
    'rep.modal.packet.razvilki.single_location': 'Single-location directory, but profile configured on branch — which location to retain',
    'rep.modal.packet.razvilki.street_absent': 'Directory has no street address at all — whether to publish it here for the first time',

    // GmbCommandModal
    'rep.modal.gmb.title': 'Google Business Profile Command Center',
    'rep.modal.gmb.main_canon': 'Main Canon',
    'rep.modal.gmb.branch': 'Branch',
    'rep.modal.gmb.synced_at': 'Synced: {date}',
    'rep.modal.gmb.not_synced': 'Never synced',
    'rep.modal.gmb.btn_syncing': 'Syncing...',
    'rep.modal.gmb.btn_sync_now': 'Sync Now',
    'rep.modal.gmb.btn_open_maps': 'Open Maps',
    'rep.modal.gmb.tab_audit': 'AI 7-Factor Audit',
    'rep.modal.gmb.tab_posts': 'Google Posts Auto-Pilot',
    'rep.modal.gmb.tab_reviews': 'Reviews & AI Replies',
    'rep.modal.gmb.tab_services': 'Services Catalog',
    'rep.modal.gmb.tab_autopilot': '24h Auto-Pilot Settings',
    'rep.modal.gmb.not_connected_warning': 'Google Business Profile is not connected. Views and calls data will become available after authorizing in Google My Business.',
    'rep.modal.gmb.status.unknown_query_failed': 'Listing status UNKNOWN: query failed.',
    'rep.modal.gmb.status.failure_reason': 'Failure reason: {reason}. No numbers below are substituted instead of a real response — there simply are none. {warning}',
    'rep.modal.gmb.status.pending_response': 'API response not yet received: listing status is not shown because it is unknown.',
    'rep.modal.gmb.status.sync_error': 'Last sync ended with an error.',
    'rep.modal.gmb.status.reason_not_specified': 'reason not specified',
    'rep.modal.gmb.status.synced': 'Listing synced.',
    'rep.modal.gmb.status.sync_moment': 'Sync timestamp (server): {date}',
    'rep.modal.gmb.status.not_connected_state': 'Listing status: NOT_CONNECTED.',
    'rep.modal.gmb.status.unrecognized': 'Listing status unrecognized: {status}.',
    'rep.modal.gmb.status.known_values': 'Known values: NOT_CONNECTED, synced, pending, error.',
    'rep.modal.gmb.metrics_30d_title': 'Google 30-Day Metrics',
    'rep.modal.gmb.metrics_source_hint': 'source: API response; dash means “not measured”',
    'rep.modal.gmb.metric.views': 'Views',
    'rep.modal.gmb.metric.searches': 'Searches',
    'rep.modal.gmb.metric.calls': 'Calls',
    'rep.modal.gmb.metric.website_clicks': 'Website Clicks',
    'rep.modal.gmb.metric.directions': 'Directions',
    'rep.modal.gmb.metric.rating': 'Rating',
    'rep.modal.gmb.metric.reviews': 'Reviews',
    'rep.modal.gmb.metric.audit_score': 'Audit Score',
    'rep.modal.gmb.tooltip.syncing': 'Requesting API: please wait for response.',
    'rep.modal.gmb.tooltip.no_account': 'Google account connection required',
    'rep.modal.gmb.tooltip.no_scope': 'Re-authorization in Google required with Business Profile management permissions',
    'rep.modal.gmb.tooltip.no_can_sync': 'Server did not confirm sync readiness: can_sync flag not received',
    'rep.modal.gmb.tooltip.server_prefix': '{hint}. Server: {msg}',
    'rep.modal.gmb.tooltip.status_unknown': 'Listing status unknown: API did not respond, nothing to query.',
    'rep.modal.gmb.tooltip.not_connected': 'Listing not connected to Google: nothing to query.',
    'rep.modal.gmb.tooltip.ready': 'Query Google and update sync timestamp.',
    'rep.modal.gmb.toast.sync_ok': 'Google responded: sync timestamp updated.',
    'rep.modal.gmb.toast.sync_skipped': 'No sync with Google: {reason}. Below is the LOCAL listing state, not Google response.',
    'rep.modal.gmb.toast.sync_failed': 'Sync NOT executed: {reason}',
    'rep.modal.gmb.toast.audit_ok': 'Audit completed. Score: {score}',
    'rep.modal.gmb.toast.audit_failed': 'Audit NOT executed: {reason}',
    'rep.modal.gmb.toast.google_auth_opened': 'Google Authorization opened in a new tab. Please approve and return.',
    'rep.modal.gmb.toast.google_auth_failed': 'Could not initiate Google connection',
    'rep.modal.gmb.toast.text_copied_open_google': '📋 Text copied! Opening Google profile — click “+ Add update” and paste text (Cmd+V).',
    'rep.modal.gmb.toast.post_content_empty': 'Please generate or enter post content first',
    'rep.modal.gmb.toast.draft_generated': 'AI Post Draft generated with Gemini 2.5',
    'rep.modal.gmb.toast.draft_failed': 'Draft NOT created: {reason}',
    'rep.modal.gmb.toast.post_empty': 'Post content cannot be empty',
    'rep.modal.gmb.toast.post_live': 'Google Post published live to Google Maps listing!',
    'rep.modal.gmb.toast.post_copied_open': '📋 Post text copied to clipboard! Open Google profile with the button below and paste (Cmd+V).',
    'rep.modal.gmb.toast.post_failed': 'Post NOT saved: {reason}',
    'rep.modal.gmb.toast.post_marked_live': 'Post confirmed as published on Google Maps!',
    'rep.modal.gmb.toast.mark_failed': 'Could not save published mark: {reason}',
    'rep.modal.gmb.toast.reply_draft_failed': 'Reply draft NOT created: {reason}',
    'rep.modal.gmb.toast.reply_live': 'Attorney reply published to Google Maps!',
    'rep.modal.gmb.toast.reply_failed': 'Reply NOT published: {reason}',
    'rep.modal.gmb.toast.autopilot_saved': 'Autopilot settings saved by API.',
    'rep.modal.gmb.toast.autopilot_failed': 'Autopilot settings NOT saved: {reason}',
    'rep.modal.gmb.toast.photos_max_50': 'You can upload up to 50 photos at a time.',
    'rep.modal.gmb.toast.photos_uploaded': 'Successfully uploaded {count} attorney photos to media vault!',
    'rep.modal.gmb.toast.photos_error': 'Error uploading photos',
    'rep.modal.gmb.toast.random_photo_picked': 'Random attorney photo selected!',
    'rep.modal.gmb.toast.services_not_pushed': 'Services are not pushed to Google from here: this screen has no catalog export endpoint.',
    'rep.modal.gmb.audit.no_measurement': 'no measurement',
    'rep.modal.gmb.audit.measured_at': 'Measured: {date}',
    'rep.modal.gmb.audit.time_unknown': 'Measurement time unknown',
    'rep.modal.gmb.audit.audited_by_model': 'Audited by model: {model}',
    'rep.modal.gmb.audit.model_not_named': 'model not specified',
    'rep.modal.gmb.audit.health_score_title': 'Listing Optimization Health Score',
    'rep.modal.gmb.audit.no_audit_summary': 'Audit was not conducted: no summary.',
    'rep.modal.gmb.audit.btn_repeat': 'Repeat deep audit',
    'rep.modal.gmb.audit.repeat_disabled_hint': 'audit is being reworked: scores will be computed from measurements',
    'rep.modal.gmb.audit.factors_title': '7 Core Local Ranking Factors',
    'rep.modal.gmb.audit.factor.nap_consistency': 'Consistency of address, phone, and bar directory entry',
    'rep.modal.gmb.audit.factor.categories': 'Primary and additional listing categories',
    'rep.modal.gmb.audit.factor.services_catalog': 'Completeness of services catalog',
    'rep.modal.gmb.audit.factor.visual_media': 'Listing photos and visual media',
    'rep.modal.gmb.audit.factor.posts_cadence': 'Posting cadence and regularity',
    'rep.modal.gmb.audit.factor.review_velocity': 'Review velocity and replies',
    'rep.modal.gmb.audit.factor.local_prominence': 'Visibility in local search results',
    'rep.modal.gmb.audit.fixes_title': 'Actionable AI Improvements & Gaps',
    'rep.modal.gmb.posts.api_connected': 'Google Business Profile API: Connected & Authorized',
    'rep.modal.gmb.posts.api_status': 'Google Business Profile API: Connection Status',
    'rep.modal.gmb.posts.api_authorized_for': 'Authorized for {email}. Posts publish directly live to Google Maps.',
    'rep.modal.gmb.posts.api_link_hint': 'Link {email} with Business Profile scope to enable direct 1-click live publishing, or use 📋 Copy & Open below.',
    'rep.modal.gmb.posts.btn_connecting': 'Connecting...',
    'rep.modal.gmb.posts.btn_connect_auto': '🔑 Enable auto-publishing in Google',
    'rep.modal.gmb.posts.btn_open_profile': 'Open profile in Google',
    'rep.modal.gmb.posts.create_title': 'Create & Publish Google Local Post',
    'rep.modal.gmb.posts.create_subtitle': 'Published directly to Google Maps under {firm} in {city}.',
    'rep.modal.gmb.posts.btn_generating': 'Generating...',
    'rep.modal.gmb.posts.btn_generate': '✨ Generate with Gemini',
    'rep.modal.gmb.posts.topic_label': 'Legal Topic or News Trigger (Optional)',
    'rep.modal.gmb.posts.topic_placeholder': 'e.g., New York Probate Timeline, Wills vs. Revocable Trusts, Estate Planning Consultations',
    'rep.modal.gmb.posts.suggested_label': 'Suggested:',
    'rep.modal.gmb.posts.content_label': 'Post Content (up to 1,500 characters)',
    'rep.modal.gmb.posts.content_placeholder': 'Enter post text or click “Generate with Gemini” above...',
    'rep.modal.gmb.posts.media_label': 'Attached Attorney / Office Photo (Google Maps Post Media)',
    'rep.modal.gmb.posts.btn_uploading': 'Uploading...',
    'rep.modal.gmb.posts.btn_upload_bulk': '+ Upload photo batch (up to 50)',
    'rep.modal.gmb.posts.btn_collapse_gallery': 'Collapse gallery',
    'rep.modal.gmb.posts.btn_choose_gallery': 'Choose from gallery ({count})',
    'rep.modal.gmb.posts.will_attach_hint': 'Will be attached to Google Maps post',
    'rep.modal.gmb.posts.btn_random_photo': '🎲 Random',
    'rep.modal.gmb.posts.btn_remove_photo': 'Remove photo',
    'rep.modal.gmb.posts.no_photo_attached': 'No photo attached. Click',
    'rep.modal.gmb.posts.choose_from_gallery_link': 'choose from gallery',
    'rep.modal.gmb.posts.or_upload_new': 'or upload new attorney photos.',
    'rep.modal.gmb.posts.media_library_title': 'Attorney media library ({count} photos)',
    'rep.modal.gmb.posts.category_all': 'All',
    'rep.modal.gmb.posts.cta_button_label': 'Call-to-Action Button',
    'rep.modal.gmb.posts.cta_book': 'Book Appointment',
    'rep.modal.gmb.posts.cta_call': 'Call Now',
    'rep.modal.gmb.posts.cta_learn_more': 'Learn More',
    'rep.modal.gmb.posts.cta_url_label': 'CTA Destination URL',
    'rep.modal.gmb.posts.btn_copy_open': '📋 Copy & Open in Google',
    'rep.modal.gmb.posts.btn_publishing': 'Publishing...',
    'rep.modal.gmb.posts.btn_publish': 'Publish to Google Maps',
    'rep.modal.gmb.posts.recent_title': 'Recent Google Posts ({count})',
    'rep.modal.gmb.posts.badge_live': 'LIVE ON GOOGLE MAPS',
    'rep.modal.gmb.posts.badge_saved_os': 'NOT ON GOOGLE MAPS (SAVED IN OS)',
    'rep.modal.gmb.posts.saved_os_hint': '⚠️ Saved in OS. Click to copy and paste into Google:',
    'rep.modal.gmb.posts.btn_auto_pub': '🔑 Auto-publish',
    'rep.modal.gmb.posts.btn_published_mark': '✓ Published',
    'rep.modal.gmb.posts.no_posts_empty': 'No local posts recorded yet. Click “Generate with Gemini” to draft your first weekly post.',
    'rep.modal.gmb.reviews.title': 'Google Maps Reviews ({count})',
    'rep.modal.gmb.reviews.signature_hint': 'The signature on the reply is only the one explicitly provided by the person.',
    'rep.modal.gmb.reviews.coverage': '100% Response Coverage',
    'rep.modal.gmb.reviews.badge_replied': 'Replied',
    'rep.modal.gmb.reviews.badge_pending': 'Pending Response',
    'rep.modal.gmb.reviews.official_reply': 'Official Reply',
    'rep.modal.gmb.reviews.official_reply_by': 'Official Reply — {author}',
    'rep.modal.gmb.reviews.btn_publish_reply': 'Publish Reply to Google',
    'rep.modal.gmb.reviews.btn_drafting': 'Drafting...',
    'rep.modal.gmb.reviews.btn_draft_reply': '✨ Draft Attorney AI Reply',
    'rep.modal.gmb.reviews.empty': 'This location currently has no Google reviews recorded. New reviews will be automatically ingested every 24 hours.',
    'rep.modal.gmb.services.title': 'Google Maps Services Catalog',
    'rep.modal.gmb.services.subtitle': 'Canonical practice areas published on this Google Business Profile listing.',
    'rep.modal.gmb.services.btn_push': 'Push Services to GMB',
    'rep.modal.gmb.services.default_category': 'Immigration Practice',
    'rep.modal.gmb.services.verified': 'Verified',
    'rep.modal.gmb.autopilot.title': '24-Hour Autonomous GMB Settings',
    'rep.modal.gmb.autopilot.subtitle': 'Configure Celery Beat background automation, weekly posting, and review reply bots.',
    'rep.modal.gmb.autopilot.sync_title': '24-Hour Full Background Sync',
    'rep.modal.gmb.autopilot.sync_desc': 'Pulls new reviews, updates rating averages, and runs 7-factor audit every night at 03:00 UTC.',
    'rep.modal.gmb.autopilot.posts_title': 'Weekly Google Posts Auto-Pilot',
    'rep.modal.gmb.autopilot.posts_desc': 'Generates and publishes weekly estate planning and probate updates with a consultation call if no post was made in 7 days.',
    'rep.modal.gmb.autopilot.reviews_title': 'Auto-Reply to 5-Star Reviews',
    'rep.modal.gmb.autopilot.reviews_desc': 'Automatically publishes personalized, warm attorney responses to all incoming 5★ reviews.',
    'rep.modal.gmb.autopilot.btn_save': 'Save Auto-Pilot Configuration',


    // ── Новые ключи (GrowthWorkspace & bilingual) ─────────────
    'rep.error.db_read': 'database read error',
    'rep.error.db_read_unmeasured': 'not measured: database read error',
    'rep.error.reason_not_specified': 'reason not specified',
    'rep.auth.connected_verified': 'Google Business Profile connected: publishing permission verified.',
    'rep.auth.connected_no_scope': 'Google connected, but Business Profile publishing permission was NOT confirmed.',
    'rep.auth.not_confirmed': 'Returned from Google, but connection status was NOT confirmed. Assume not connected.',
    'rep.auth.verify_failed': 'Returned from Google, but could not verify connection: {error}. State UNKNOWN.',
    'rep.auth.incomplete': 'Could not complete Google authorization.',
    'rep.auth.error_detail': 'Google authorization error: {detail}',
    'rep.office.no_profiles': 'No profiles: nothing to open. Profile list provided by `profiles` prop.',
    'rep.office.badge_main_canon': 'Main canon office',
    'rep.office.badge_branch': 'Branch',
    'rep.office.badge_main_canon_caps': 'MAIN CANON OFFICE',
    'rep.office.badge_branch_caps': 'BRANCH',
    'rep.office.desc_main_canon': 'Main canon office.',
    'rep.office.desc_branch': 'Canon branch.',
    'rep.office.profile_not_linked': 'Profile not linked',
    'rep.geogrid.points_count': ' ({validObs}/{totalPoints} points)',
    'rep.geogrid.worst_keyword': '{covVal}%{pointsStr} · worst: “{keyword}”',
    'rep.state.not_specified': 'not specified',
    'rep.audit.querying': 'Querying server for latest audit…',
    'rep.audit.query_failed': 'Could not query server: {error}. This does NOT mean no audit exists.',
    'rep.audit.server_timeout': 'No response on latest audit: server timed out.',
    'rep.audit.never_run': 'Audit has never run.',
    'rep.audit.cache_unreadable': 'Latest audit exists but its record cannot be read — this is NOT “no audits exist”.',
    'rep.audit.invalid_tenant': 'Refusal: tenant identifier was rejected by server.',
    'rep.audit.no_report': 'Response arrived without report: nothing to display.',
    'rep.pulse.loading_inquiry_data': 'Loading inquiry data from OS database...',
    'rep.pulse.failed_inquiry_data': 'Failed to load inquiry data: {error}',
    'rep.pulse.cutoff_27_08': '27.08 cutoff',
    'rep.gsc.chart_tooltip': '{date}: {clicks}, {impressions}, CTR {ctr}%, {pos_label} {pos}',
    'rep.gsc.pos_label': 'pos.',
    'rep.locations.source_of_truth_main': 'Source of truth for main profile',
    'rep.locations.title': 'Practice Locations & Truth Anchors',
    'rep.locations.subtitle': 'Source of Truth data, attorney bios, verified addresses, and agent squad assignments.',
    'rep.office.metric.squad': 'Squad',
    'rep.office.metric.accuracy': 'Accuracy',
    'rep.office.metric.status': 'Status',

    // ── Stage 1: Google Posts & Daily Verification ──────────────
    'rep.tab.gmb_posts': 'Google Posts',
    'rep.header.tab_gmb_posts_title': 'Google Posts & Autopilot',
    'rep.header.tab_gmb_posts_desc': 'Google Business Profile local posts, compliance guard, and daily autopilot',
    'rep.gmb.daily_title': 'Published in last 24h',
    'rep.gmb.daily_subtitle': 'Daily review of published Google Posts for Manhattan Office (299 Broadway)',
    'rep.gmb.unverified_count': '{count} unverified',
    'rep.gmb.all_verified': 'All posts verified',
    'rep.gmb.verified_at': 'Verified: {by}, {date}',
    'rep.gmb.not_verified': 'Not verified yet',
    'rep.gmb.btn_verify': 'Mark as Verified',
    'rep.gmb.btn_verifying': 'Verifying...',
    'rep.gmb.btn_patch': 'Edit in Google',
    'rep.gmb.btn_delete': 'Remove',
    'rep.gmb.btn_approve': 'Approve',
    'rep.gmb.btn_approving': 'Approving...',
    'rep.gmb.guard_passed_badge': 'Guard: PASSED',
    'rep.gmb.guard_violations_badge': 'Guard: REJECTED ({count})',
    'rep.gmb.status.published': 'Published (Google API)',
    'rep.gmb.status.published_manually': 'Published Manually',
    'rep.gmb.status.needs_review': 'Katifi Remarks — Owner Decision Needed',
    'rep.gmb.status.pending_approval': 'Pending Approval',
    'rep.gmb.status.pending_rewrite': 'Pending Rewrite Approval',
    'rep.gmb.status.publish_unconfirmed': 'Publication Unconfirmed',
    'rep.gmb.status.removed_by_google': 'Removed by Google',
    'rep.gmb.status.approved': 'Approved',
    'rep.gmb.btn_approve_rewrite': 'Approve Rewrite',
    'rep.gmb.suggested_rewrite_title': 'Suggested AI rewrite (not published):',
    'rep.gmb.status.expired': 'Expired',
    'rep.gmb.status.rejected': 'Rejected',
    'rep.gmb.status.failed': 'Failed (Google API Error)',
    'rep.gmb.status.draft': 'Draft',
    'rep.gmb.filter_pending_approval': 'Pending Approval',
    'rep.gmb.filter_manual': 'Published Manually',
    'rep.gmb.btn_mark_manual': 'Mark Published Manually',
    'rep.gmb.btn_copy_text': 'Copy Text',
    'rep.gmb.btn_copy_link': 'Copy Link',
    'rep.gmb.copied_text_success': 'Post text copied to clipboard',
    'rep.gmb.copied_link_success': 'Post link copied to clipboard',
    'rep.gmb.manual_modal_title': 'Mark as Published Manually',
    'rep.gmb.manual_modal_desc': 'Specify the Google post URL after publishing in Google, then confirm manual publication.',
    'rep.gmb.manual_modal_url_label': 'Google Post URL',
    'rep.gmb.manual_modal_url_placeholder': 'https://posts.gle/... or https://maps.app.goo.gl/...',
    'rep.gmb.manual_modal_btn_confirm': 'Confirm Manual Publication',
    'rep.gmb.manual_modal_btn_cancel': 'Cancel',
    'rep.gmb.manual_toast_success': 'Post marked as published manually',
    'rep.gmb.manual_url_required': 'Google post URL is required (posts.gle, maps.app.goo.gl, google.com/maps)',
    'rep.gmb.btn_approve_and_publish': 'Approve',
    'rep.gmb.btn_reject': 'Reject',
    'rep.gmb.reject_title': 'Reject Post Draft',
    'rep.gmb.reject_reason_label': 'Reason for rejection',
    'rep.gmb.reject_reason_placeholder': 'Explain why the draft is rejected...',
    'rep.gmb.reject_submit': 'Reject Draft',
    'rep.gmb.toast_rejected': 'Draft rejected',
    'rep.gmb.filter_all': 'All Locations',
    'rep.gmb.filter_unverified': 'Unverified Only',
    'rep.gmb.card_manhattan': 'Manhattan Office (299 Broadway)',
    'rep.gmb.select_office_hint': 'Please select an office for the post',
    'rep.gmb.select_office_placeholder': '-- Select office --',
    'rep.gmb.confirm_delete': 'Remove this post from Google Maps? This action will permanently delete the post via API.',
    'rep.gmb.confirm_delete_manual': 'This post was published manually — please delete it directly in Google; OS will only update its internal record. Proceed?',
    'rep.gmb.manual_edit_warning': 'This post was published manually — please edit it directly in Google; OS will only update its internal record.',
    'rep.gmb.patch_title': 'Edit Post in Google',
    'rep.gmb.patch_summary_label': 'Post Text',
    'rep.gmb.patch_save': 'Update via Google API',
    'rep.gmb.patch_cancel': 'Cancel',
    'rep.gmb.patch_saving': 'Updating...',
    'rep.gmb.toast_verified': 'Post marked as verified by {by}',
    'rep.gmb.toast_deleted': 'Post removed from Google Maps',
    'rep.gmb.toast_patched': 'Post updated via Google Maps API',
    'rep.gmb.toast_approved': 'Post approved and processed',
    'rep.gmb.btn_rewrite': 'Rewrite',
    'rep.gmb.rewrite_title': 'Rewrite Google Post with AI',
    'rep.gmb.rewrite_feedback_label': 'What needs to be changed? (Instructions for AI)',
    'rep.gmb.rewrite_feedback_placeholder': 'e.g. Focus more on probate administration or adjust tone...',
    'rep.gmb.rewrite_submitting': 'Rewriting with AI…',
    'rep.gmb.toast_rewritten': 'Post rewritten and updated in Google',
    'rep.modal.gmb.toast.select_office': 'Please select an office before generating a post',
    'rep.gmb.empty_daily': 'No posts published or queued in the last 7 days.',
    'rep.gmb.posts_per_week_counter': 'Published in last 7 days: {count} of {target}',
    'rep.gmb.last_published_at': 'Last published: {date}',
    'rep.gmb.never_published': 'No publications yet',
    'rep.gmb.rhythm_warning_title': 'Weekly Rhythm Warning',
    'rep.gmb.rhythm_warning_msg': 'You have already published {count} posts in the last 7 days (target pace is {target}). Publishing too frequently may trigger Google suspensions (case 23.10). Are you sure you want to proceed?',
    'rep.gmb.waiting_for_autopublish': 'Waiting for auto-publish',
    'rep.gmb.autopublish_stopped_banner': 'Auto-publish stopped: {reason}, {date}',
    'rep.gmb.btn_resume_autopublish': 'Resume auto-publish',
    'rep.gmb.resume_modal_title': 'Resume Auto-Publish Pipeline',
    'rep.gmb.resume_modal_desc': 'The auto-publish pipeline was stopped. As the firm owner, please provide a mandatory explanation to clear the stop flag and resume automatic publishing to Google.',
    'rep.gmb.resume_comment_label': 'Owner explanation (mandatory)',
    'rep.gmb.resume_comment_placeholder': 'Explain why it is safe to resume auto-publish (e.g. Google card reviewed, rejection resolved)...',
    'rep.gmb.btn_confirm_resume': 'Resume Pipeline',
    'rep.gmb.btn_resuming': 'Resuming…',
    'rep.gmb.toast_resumed': 'Auto-publish pipeline resumed successfully',
    'rep.gmb.resume_comment_required': 'Owner explanation is mandatory to resume pipeline',
    'rep.gmb.next_autopublish_at': 'next auto-publish not before {date}',
    'rep.gmb.next_autopublish_ready': 'ready for auto-publish',
    'rep.gmb.next_autopublish_target_reached': 'weekly limit reached',
    'rep.gmb.next_autopublish_stopped': 'auto-publish stopped',
    'rep.gmb.reason_google_rejected': 'Google rejected post',
    'rep.gmb.reason_three_consecutive_failures': '3 consecutive bridge failures',
    'rep.gmb.reason_publish_unconfirmed': 'Publication unconfirmed after submit',
    'rep.gmb.reason_unresolved_claim': 'Unresolved bridge claim exists',
    'rep.gmb.reason_office_not_in_canon': 'Office not found in canonical configuration',
    'rep.gmb.reason_google_posting_disabled': 'Google disabled posting on this profile',
    'rep.gmb.google_posting_disabled_banner': 'Google disabled posting on this profile on {date}. Auto-publish cannot be resumed until posting is restored by Google.',
    'rep.gmb.btn_google_policy_removed': 'Google Policy Removal (Violation)',
    'rep.gmb.google_policy_modal_title': 'Google Policy Removal',
    'rep.gmb.google_policy_modal_desc': 'Record that Google removed this post due to content policy violation and disabled posting on this profile. This sets the pipeline stop flag until Google restores posting.',
    'rep.gmb.google_notice_label': 'Google Notice Text / Subject (mandatory)',
    'rep.gmb.google_notice_placeholder': "e.g. Estate Law New York, your post violates Google's content policy (Routing ID: DPNB)...",
    'rep.gmb.google_policy_comment_label': 'Owner comment (mandatory)',
    'rep.gmb.google_policy_comment_placeholder': 'Explain the action taken or appeal plan...',
    'rep.gmb.btn_confirm_google_policy_removed': 'Confirm Removal by Google',
    'rep.gmb.toast_google_policy_removed': 'Post marked as removed by Google. Pipeline stopped.',
    'rep.gmb.btn_restore_google_posting': 'Google Restored Posting',
    'rep.gmb.restore_google_modal_title': 'Confirm Google Restored Posting',
    'rep.gmb.restore_google_modal_desc': 'Only proceed if Google support or appeal confirmed that posting on this Business Profile has been re-enabled.',
    'rep.gmb.restore_google_comment_label': 'Confirmation details and date (mandatory)',
    'rep.gmb.restore_google_comment_placeholder': 'e.g. Appeal granted on 11/02/2026, confirmed posting functionality active in Google Business Profile...',
    'rep.gmb.btn_confirm_restore_google_posting': 'Restore Posting',
    'rep.gmb.toast_google_posting_restored': 'Google posting restored. Auto-publish pipeline resumed.',
    'rep.gmb.publish_unconfirmed_hint': 'Post was submitted to Google, but confirmation was not received. Please check the Google Business Profile card manually to confirm or revert the publication.',
    'rep.gmb.unresolved_claim_title': 'Bridge claimed post, no result received — check Google Business Profile card',
    'rep.gmb.unresolved_claim_hint': 'The bridge claimed this post (bridge_claimed), but no result was reported. Please check the Google Business Profile card: if published, confirm with the Google URL; if not, return it to approved.',
    'rep.gmb.btn_scroll_to_post': 'Go to post',
    'rep.gmb.btn_confirm_published': 'Confirm: Published (Google URL)',
    'rep.gmb.btn_revert_to_approved': 'Not Published (Return to Approved)',
    'rep.gmb.btn_clear_claim': 'Not Published (Clear Claim)',
    'rep.gmb.confirm_pub_modal_title': 'Confirm Publication in Google',
    'rep.gmb.confirm_pub_modal_desc': 'If you checked the Google Business Profile card and the post was indeed published, enter the Google Post URL and your confirmation comment.',
    'rep.gmb.confirm_pub_url_label': 'Google Post URL (mandatory)',
    'rep.gmb.confirm_pub_comment_label': 'Owner verification comment (mandatory)',
    'rep.gmb.confirm_pub_comment_placeholder': 'e.g. Verified live post on Google Business Profile card manually...',
    'rep.gmb.revert_app_modal_title': 'Return to Approved (Not Published)',
    'rep.gmb.revert_app_modal_desc': 'If the post did NOT appear on Google Business Profile, confirm returning it to approved status. Please provide an owner explanation comment.',
    'rep.gmb.revert_app_comment_label': 'Owner explanation comment (mandatory)',
    'rep.gmb.revert_app_comment_placeholder': 'e.g. Checked Google Business Profile card, post was not created...',
    'rep.gmb.clear_claim_modal_title': 'Clear Bridge Claim',
    'rep.gmb.clear_claim_modal_desc': 'Confirm that this post did not publish on Google Business Profile. This will clear the bridge lock without approving the post.',
    'rep.gmb.toast_confirm_published': 'Post confirmed as published',
    'rep.gmb.toast_revert_approved': 'Post returned to approved status',
    'rep.gmb.toast_claim_cleared': 'Bridge claim cleared',
    'rep.gmb.btn_verify_katifi': 'Verify with Katifi',
    'rep.gmb.btn_verifying_katifi': 'Katifi Verifying...',
    'rep.gmb.katifi_badge_pass': 'Katifi: PASSED',
    'rep.gmb.katifi_badge_fail': 'Katifi: REMARKS ({count})',
    'rep.gmb.katifi_badge_not_reviewed': 'Katifi: Not Reviewed',
    'rep.gmb.katifi_override_title': 'Approve Despite Katifi Remarks',
    'rep.gmb.katifi_override_desc': 'Katifi identified compliance remarks. As firm owner, enter a mandatory comment explaining your approval override:',
    'rep.gmb.katifi_override_comment_label': 'Owner Override Comment',
    'rep.gmb.katifi_override_btn': 'Approve with Override',
    'rep.gmb.katifi_override_comment_required': 'Owner override comment is required',
    'rep.gmb.open_in_google_maps': 'Open in Google',
    'rep.gmb.create_tab_title': 'Draft / Publish Post',
    'rep.gmb.select_office_label': 'Select Location',
    'rep.gmb.select_language_label': 'Language',
    'rep.gmb.language_en': 'English (EN)',
    'rep.gmb.language_ru': 'Russian (RU)',
    'rep.gmb.select_topic_label': 'Topic (from site services)',
    'rep.gmb.btn_publish_now': 'Publish Now',
    'rep.gmb.btn_save_draft': 'Save draft',
    'rep.gmb.btn_saving_draft': 'Saving draft...',
    'rep.gmb.toast_draft_saved': 'Draft saved to OS and queued for Katifi review',
    'rep.gmb.view_in_google': 'View in Google',
    'rep.gmb.posts_api_disabled_reason': 'Google has not opened the Posts API for this project. Save the draft for Katifi review and owner approval; after approval, publish manually in Google.',
    'rep.gmb.btn_copy_text': 'Copy text',
    'rep.gmb.copy_text_success': 'Text copied to clipboard',
    'rep.gmb.gemini_key_missing': 'GEMINI_API_KEY is not configured',
    'rep.gmb.no_locations_hint': 'Create the office profile first',
    'rep.gmb.link_office_profiles': 'Office Profiles',
    'rep.pulse.unmeasured.gbp_not_measured': 'not measured: no performance measurement recorded',
    'rep.pulse.unmeasured.gbp_performance_failed': 'not measured: measurement failed',
    'rep.pulse.unmeasured.gbp_stale': 'not measured: stale data',
    'rep.pulse.unmeasured.gbp_account_not_set': 'not measured: Google Business Profile account not configured',
    'rep.pulse.unmeasured.gbp_account_not_connected': 'not measured: Google account not connected for {email}',
    'rep.pulse.unmeasured.location_not_visible_to_token': 'not measured: location {place_id} is not visible to token owner {email}',
    'rep.pulse.unmeasured.performance_api_error': 'not measured: Google Performance API error {status_code}: {message}',
};

const RU = {
    // ── Состояния и служебные ────────────────────────────────────────────────
    'rep.state.loading': 'Загрузка…',
    'rep.state.not_measured': 'не измерено',
    'rep.state.not_checked': 'не проверялось',
    'rep.state.no_source': 'источника нет',
    'rep.state.source_not_connected': 'источник не подключён',
    'rep.state.db_error': 'сбой чтения базы',
    'rep.state.zero': '0',
    'rep.state.dash': '—',

    // ── Вкладки экрана ───────────────────────────────────────────────────────
    'rep.tab.reputation': 'Репутация и доверие',
    'rep.tab.marketing': 'Пульс маркетинга',
    'rep.tab.citations': 'Каталоги и цитирования',
    'rep.tab.locations': 'Профили офисов',

    // ── Окна времени ─────────────────────────────────────────────────────────
    'rep.timeframe.30d': '30 дней',
    'rep.timeframe.90d': '90 дней',

    // ── Верхний ряд карточек Reputation & Trust ──────────────────────────────
    'rep.card.nap_index.title': 'Индекс каталогов NAP',
    'rep.card.nap_index.subtitle': 'Взвешенная точность по каталогам юристов',
    'rep.card.prescriptions.title': 'Замечания к исправлению',
    'rep.card.prescriptions.subtitle': 'Всего открытых замечаний и расхождений',
    'rep.card.ai_citations.title': 'Цитаты нейросетей',
    'rep.card.ai_citations.subtitle': 'Упоминания бренда в ответах ИИ-поиска',
    'rep.card.offices.title': 'Офисы в каноне',
    'rep.card.offices.subtitle': 'Подтверждённые адреса практики',
    'rep.card.denominator': '{n} из {m}',
    'rep.card.measured_on': 'замер от {date}',
    'rep.card.measured_by': 'метод: {method}',

    // ── Сводные карточки Marketing Pulse ─────────────────────────────────────
    'rep.pulse.total_inquiries.title': 'Всего обращений',
    'rep.pulse.total_inquiries.active': 'активных обращений за {timeframe}',
    'rep.pulse.booked_consults.title': 'Записи на консультацию',
    'rep.pulse.booked_consults.caption': 'ведётся вне OS',
    'rep.pulse.booked_consults.reason': 'в OS записано {zapisi} {na_obrashcheniy} — запись ведётся вне OS',
    'rep.pulse.avg_conversion.title': 'Средняя конверсия',
    'rep.pulse.avg_conversion.caption': 'статусы не ведутся',
    'rep.pulse.avg_conversion.reason': 'статусы не ведутся — {n} из {m} «NEW»',
    'rep.pulse.revenue_pipeline.title': 'Пайплайн выручки',
    'rep.pulse.revenue_pipeline.caption': 'оплат в OS нет',
    'rep.pulse.revenue_pipeline.reason': 'оплат в OS нет',
    'rep.pulse.organic_reach.title': 'Органический охват',
    'rep.pulse.conversion_not_measured': 'конверсия не измеряется',

    // ── Неизмеренные величины (причины) ──────────────────────────────────────
    'rep.pulse.unmeasured.pending_gbp_api': 'не измерено: Google Business Profile API не подключён',
    'rep.pulse.unmeasured.referral_source_not_asked': 'при приёме не спрашивается, откуда узнали',
    'rep.pulse.unmeasured.gsc_not_connected': 'Search Console не подключён',

    // ── Отсечка 90 дней ──────────────────────────────────────────────────────
    'rep.pulse.cutoff.intake_lost_leads_before_date': 'до 27.08 приём терял заявки (разбор 26.08), данные до этой даты неполные',

    // ── Множественное число: Записи (Bookings) ───────────────────────────────
    'rep.plural.zapisi.one': '{n} запись',
    'rep.plural.zapisi.few': '{n} записи',
    'rep.plural.zapisi.many': '{n} записей',

    // ── Множественное число: на M обращений (Per M Inquiries) ─────────────────
    'rep.plural.na_obrashcheniy.one': 'на {m} обращение',
    'rep.plural.na_obrashcheniy.few': 'на {m} обращения',
    'rep.plural.na_obrashcheniy.many': 'на {m} обращений',

    // ── Множественное число: Обращения (Inquiries) ───────────────────────────
    'rep.plural.obrashcheniy.one': '{n} обращение',
    'rep.plural.obrashcheniy.few': '{n} обращения',
    'rep.plural.obrashcheniy.many': '{n} обращений',

    // ── Множественное число: Звонки (Calls) ──────────────────────────────────
    'rep.plural.zvonkov.one': '{n} звонок',
    'rep.plural.zvonkov.few': '{n} звонка',
    'rep.plural.zvonkov.many': '{n} звонков',

    // ── Множественное число: Сессии (Sessions) ───────────────────────────────
    'rep.plural.sessiy.one': '{n} сессия',
    'rep.plural.sessiy.few': '{n} сессии',
    'rep.plural.sessiy.many': '{n} сессий',

    // ── Множественное число: Клики (Clicks) ──────────────────────────────────
    'rep.plural.klikov.one': '{n} клик',
    'rep.plural.klikov.few': '{n} клика',
    'rep.plural.klikov.many': '{n} кликов',

    // ── Множественное число: Точки геосетки (Points) ─────────────────────────
    'rep.plural.tochkam.one': 'по {n} точке, где карточка найдена',
    'rep.plural.tochkam.few': 'по {n} точкам, где карточка найдена',
    'rep.plural.tochkam.many': 'по {n} точкам, где карточка найдена',

    // ── Множественное число: Отзывы (Reviews) ────────────────────────────────
    'rep.plural.otzyvov.one': '{n} отзыв',
    'rep.plural.otzyvov.few': '{n} отзыва',
    'rep.plural.otzyvov.many': '{n} отзывов',

    // ── Множественное число: Замеры (Scans) ──────────────────────────────────
    'rep.plural.zamerov.one': '{n} замер',
    'rep.plural.zamerov.few': '{n} замера',
    'rep.plural.zamerov.many': '{n} замеров',

    // ── Подписи каналов Marketing Pulse ──────────────────────────────────────
    'rep.channel.voice': '{leads} · {calls} за {timeframe}',
    'rep.channel.voice_db_error': '{leads} · не измерено: сбой чтения базы',
    'rep.channel.email': '{leads} за {timeframe}',
    'rep.channel.form': '{leads} за {timeframe}',
    'rep.channel.chat': '{leads} · {sessions} за {timeframe}',
    'rep.channel.chat_db_error': '{leads} · не измерено: сбой чтения базы',
    'rep.channel.calendar': '{leads} за {timeframe}',
    'rep.channel.other': '{leads} за {timeframe}',

    // ── Search Console (Organic Reach) ───────────────────────────────────────
    'rep.gsc.clicks': 'Клики',
    'rep.gsc.impressions': 'Показы',
    'rep.gsc.ctr': 'CTR',
    'rep.gsc.position': 'Ср. позиция',
    'rep.gsc.caption.no_source': 'источника нет',
    'rep.gsc.caption.stale_since': 'не измерено с {date}',
    'rep.gsc.caption.no_impressions': 'Search Console, данные по {date}, нет показов',
    'rep.gsc.caption.data_with_lag': 'Search Console, данные по {date}, задержка ~{lag} суток',
    'rep.gsc.stale_badge': 'устарело',
    'rep.gsc.curve_not_built': 'Кривая органического охвата не строится: {detail}',

    // ── Каталоги: Статусы (Citations & Directories) ──────────────────────────
    'rep.citations.status.MATCH': 'Совпадает',
    'rep.citations.status.MINOR_DRIFT': 'Мелкий дрифт',
    'rep.citations.status.CRITICAL_DRIFT': 'Расхождение',
    'rep.citations.status.MISSING_LISTING': 'Профиля нет',
    'rep.citations.status.NOT_MEASURED': 'Не проверялось',
    'rep.citations.status.IN_REVIEW': 'На рассмотрении',
    'rep.citations.status.NOT_COMPARABLE': 'Сравнить не с чем',

    // ── Каталоги: Маршруты ───────────────────────────────────────────────────
    'rep.citations.route.both': 'Оба канона (главный + филиал)',
    'rep.citations.route.main_only': 'Только главный',
    'rep.citations.route.human_decides': 'Решает человек (уже числится по филиалу)',

    // ── Каталоги: Замечания к NAP (Issues) ───────────────────────────────────
    'rep.citations.issue.name_mismatch': 'имя фирмы не совпадает',
    'rep.citations.issue.name_is_attorney_not_firm': 'вместо фирмы указан адвокат',
    'rep.citations.issue.name_former_used': 'указана прежняя фамилия',
    'rep.citations.issue.phone_mismatch': 'телефон не совпадает',
    'rep.citations.issue.street_mismatch': 'улица записана иначе',
    'rep.citations.issue.suite_mismatch': 'номер офиса не совпадает',
    'rep.citations.issue.city_state_zip_mismatch': 'город / штат / индекс не совпадают',
    'rep.citations.issue.website_mismatch': 'сайт не совпадает',
    'rep.citations.issue.listed_on_branch_but_route_is_main_only': 'канон велит главный, но профиль филиала',
    'rep.citations.issue.submitted_awaiting_platform': 'заявка подана, ждём площадку',

    // ── Каталоги: Следующие действия ─────────────────────────────────────────
    'rep.citations.action.await_platform': 'Ждать рассмотрения',
    'rep.citations.action.profile_absent': 'Создать профиль',
    'rep.citations.action.fix_drift': 'Исправить расхождение',
    'rep.citations.action.verify_profile': 'Сверить профиль',
    'rep.citations.action.monitor': 'В норме',

    // ── Каталоги: Методы замера ──────────────────────────────────────────────
    'rep.citations.method.api': 'API Google Business Profile',
    'rep.citations.method.human_snapshot': 'Человек, снимок',
    'rep.citations.method.snapshot_audit': 'Аудит снимка',
    'rep.citations.method.other': 'Аудит',
    'rep.citations.method.not_measured': 'не измерено',

    // ── Геосетка и профили офисов ────────────────────────────────────────────
    'rep.geogrid.google_maps_via_c2': 'Google Maps через замер C2',
    'rep.geogrid.divergence_notes': 'Расхождение между точками замера: большинство ({winnerCount} из {pointsFound}) показывают ★ {winnerRating} ({winnerReviews}). Другие значения: {otherBreakdown}',
    'rep.geogrid.comparable_diff': '{sign}{diff} п.п. к замеру от {date}{lowCov}',
    'rep.geogrid.low_coverage': ' (покрытие ниже порога)',
    'rep.geogrid.activity_feed': 'Журнал замеров',
    'rep.geogrid.no_scans': 'Замеров не зафиксировано',

    // ── Заголовки и вкладки GrowthWorkspace ──────────────────────────────────
    'rep.header.badge': 'ДВИЖОК РОСТА И РЕПУТАЦИИ',
    'rep.header.tab_marketing_title': 'Пульс привлечения и конверсии',
    'rep.header.tab_reputation_title': 'Public trust — источника измерений нет',
    'rep.header.tab_citations_title': 'Сеть верифицированных юридических каталогов',
    'rep.header.tab_locations_title': 'Локации офисов и эталонные профили',
    'rep.header.tab_marketing_desc': 'Метрики спроса по Voice AI (Sofia), Google Maps, рекомендациям клиентов и чату сайта.',
    'rep.header.tab_reputation_desc': 'Сигналы репутации отсюда НЕ измеряются: живого источника у этих карточек нет.',
    'rep.header.tab_citations_desc': 'Учётные записи, адреса и допуски. Адрес привязки берётся у держателя офисов, а не из вёрстки.',
    'rep.header.tab_locations_desc': 'Управление филиалами, запуск батлов 10 AI-агентов, настройка NAP и воркеров API.',
    'rep.tabs.locations': 'Профили офисов ({n})',

    // ── Кнопки шапки GrowthWorkspace ─────────────────────────────────────────
    'rep.action.agent_battle': 'Арена батлов агентов',
    'rep.action.source_of_truth': 'Канонический эталон',
    'rep.action.analytics': 'Аналитика',
    'rep.action.run_full_audit': 'Запустить полный аудит',

    // ── Верхний ряд карточек Reputation & Trust ──────────────────────────────
    'rep.card.open_issues.title': 'Нерешённые проблемы',
    'rep.card.open_issues.prescriptions': 'Предписаний в отчёте: {n}',
    'rep.card.open_issues.critical': 'критичных: {n}',
    'rep.card.open_issues.normal': 'обычных: {n}',
    'rep.card.open_issues.no_prescriptions': 'предписаний в отчёте нет',
    'rep.card.nap_citations.title': 'NAP и цитаты нейросетей',
    'rep.card.nap_citations.no_nap': 'NAP в отчёте нет',
    'rep.card.nap_citations.canon_unavailable': 'канон недоступен — замер NAP не проводился; ни процента, ни знаменателя здесь не показывается',
    'rep.card.nap_citations.measured_den': 'измерено {n} из {m}',
    'rep.card.nap_citations.weighted_index': 'взвешенный индекс (веса каталогов), не среднее',
    'rep.card.nap_citations.ai_citations': 'цитаты нейросетей: {our} из {total}',
    'rep.card.nap_citations.llm_not_polled': 'нейросети не опрашивались',
    'rep.card.nap_citations.no_domain': 'домен фирмы не прочитался — сравнивать не с чем',
    'rep.card.nap_citations.express_warning': 'экспресс-прогон: один повтор на подсказку вместо трёх — точность ниже',
    'rep.card.nap_citations.btn_launch': 'Запустить замер в окне аудита',
    'rep.card.entity_status.title': 'Статус публичной сущности',
    'rep.card.entity_status.canon_unavailable': 'канон недоступен',
    'rep.card.entity_status.canon_locations': 'канонических локаций',
    'rep.card.entity_status.directories_measured': 'каталогов измерено: {n} из {m}',
    'rep.card.entity_status.statement_what_measured': 'это утверждение о том, ЧТО измерено, а не оценка проверенности',
    'rep.card.nap_baseline.title': 'Базовая линия NAP',
    'rep.card.nap_baseline.no_index': 'индекса NAP в отчёте нет: ',
    'rep.card.nap_baseline.badge': 'базовая линия',
    'rep.card.nap_baseline.recorded_at': 'точка старта зафиксирована {date}; график динамики строится от трёх последовательных замеров',
    'rep.card.nap_baseline.unnamed_moment': 'момент замера не назван',
    'rep.card.nap_baseline.explanation': 'это взвешенный индекс NAP, а не «здоровье»; одна точка — не тренд',

    // ── Графики и пульс маркетинга ───────────────────────────────────────────
    'rep.pulse.title': 'Привлечение и конверсия',
    'rep.pulse.subtitle': 'Динамика спроса и конверсии',
    'rep.pulse.tracking_desc': 'Отслеживание звонков с высоким намерением, записей и органических лидов.',
    'rep.pulse.metric.leads': 'Лиды',
    'rep.pulse.metric.booked': 'Записанные звонки',
    'rep.pulse.metric.organic': 'Органический охват',
    'rep.pulse.legend.total_inquiries': 'Всего обращений (лиды)',
    'rep.pulse.legend.booked': 'Записанные консультации',
    'rep.pulse.legend.organic': 'Охват в органическом поиске',
    'rep.pulse.active_leads_data': 'по данным базы OS (активные лиды)',
    'rep.pulse.loading_leads': 'Загрузка данных обращений из базы OS...',
    'rep.pulse.error_leads': 'Не удалось получить данные обращений: {error}',
    'rep.pulse.db_error_detail': 'не измерено: сбой чтения базы данных',
    'rep.pulse.no_inquiries_in_window': 'Обращений в выбранном окне не зафиксировано.',
    'rep.pulse.booked_curve_not_built': 'Кривая записей на консультации не строится: {reason}.',
    'rep.pulse.loading_gsc': 'Загрузка данных Search Console...',
    'rep.pulse.stale_slice': 'требуется обновление среза',
    'rep.pulse.other_channels_title': 'Другие каналы привлечения и статус их измерения',
    'rep.pulse.channel_inbound_calls': 'входящих звонков: {n}',
    'rep.pulse.channel_chat_sessions': 'сессий чата: {n}',
    'rep.pulse.cutoff_prefix': 'Отсечка {date}:',

    // ── Рабочий список каталогов (CitationsWorklist) ──────────────────────────
    'rep.citations.coverage.title': 'Охват замеров',
    'rep.citations.measured_denominator': 'Измерено: {n} из {m}',
    'rep.citations.last_scan': 'Последний замер: {date}',
    'rep.citations.scan_never_run': 'не проводился',
    'rep.citations.method_human_screenshot': '(человек, снимки экрана)',
    'rep.citations.consistency_index': 'Индекс консистентности',
    'rep.citations.index_score': 'Индекс: {score}%',
    'rep.citations.no_network_scan': 'сетевого опроса не было — величина не измеряется',
    'rep.citations.weighted_explanation': 'взвешенный по весам каталогов; в знаменателе только измеренные — {n} из {m}',
    'rep.citations.active_canons': 'Действующие каноны фирмы',
    'rep.citations.role_main': 'главный, адрес виден',
    'rep.citations.role_branch': 'филиал, SAB',
    'rep.citations.street_hidden': ', улица скрыта',
    'rep.citations.source': 'Источник: {source}',
    'rep.citations.canon_not_set': 'канон не задан',
    'rep.citations.never_publish.title': 'Никогда не публиковать в общедоступных каталогах',
    'rep.citations.never_publish.desc': 'Служебная линия робота и жилые фрагменты филиала. Запрет проверяется в момент заполнения профилей.',
    'rep.citations.table.title': 'Каталоги Tier-1 и супер-доноры поиска',
    'rep.citations.table.desc': 'Площадки, формирующие 100% цитирования в поисковых LLM. Заполняйте профили в соответствии с маршрутом канона.',
    'rep.citations.btn.prepare_packet': 'Подготовить пакет правок',
    'rep.citations.btn.collapse_packet': 'Свернуть пакет правок',
    'rep.citations.empty_directories': 'Данные получены, каталогов в ответе нет — править нечего.',
    'rep.citations.warnings_parser': 'Замечания разборщика таблицы замеров:',
    'rep.citations.th.directory': 'Каталог',
    'rep.citations.th.route': 'Что подаём',
    'rep.citations.th.status': 'Состояние',
    'rep.citations.th.score_weight': 'Скор · вес',
    'rep.citations.th.action': 'Действие',
    'rep.citations.super_donor': 'Супер-донор',
    'rep.citations.scan': 'Замер: {date}',
    'rep.citations.history': 'История: ',
    'rep.citations.human_decides_desc': 'Требует решения человека: автоматическое переключение запрещено каноном',
    'rep.citations.not_comparable_desc': 'наблюдение есть, сравнить не с чем',
    'rep.citations.observed_prefix': 'замер: {address}',
    'rep.citations.unobserved': 'на снимке не видно: {items}',
    'rep.citations.weight': 'вес {weight}',
    'rep.citations.not_in_calc': 'не в расчёте',
    'rep.citations.action.open_profile': 'Открыть профиль',
    'rep.citations.action.create_profile': 'Завести профиль',
    'rep.citations.no_full_url': 'полный URL в замере не записан',
    'rep.citations.slug': 'слаг: {slug}',
    'rep.citations.error.title': 'Не смогли прочитать данные каталогов',
    'rep.citations.error.desc': 'Это отказ прибора, а не «нарушений нет»: признак — {flag}',
    'rep.citations.error.retry': 'Повторить запрос',
    'rep.citations.loading': 'Загрузка рабочего списка каталогов и канона...',
    'rep.citations.canon_not_configured': 'Канон не заведён',
    'rep.citations.canon_not_configured_desc': 'Канон цитирования для данной практики пока не заведён.',

    // ── Единая подпись замера (PodpisZamera) ──────────────────────────────────
    'rep.podpis.audit_recorded': 'Аудит зафиксирован:',
    'rep.podpis.invalid_date': '(момент в разборчивый вид не привёлся: показан как есть)',
    'rep.podpis.method': 'метод: {method}',
    'rep.podpis.method_human_screenshots': 'снимки экрана, сверенные человеком',
    'rep.podpis.method_not_stated': 'метод замера не назван',
    'rep.podpis.timestamp_not_stated': 'момент не назван',
    'rep.podpis.cache_age_title': 'точное значение: {seconds} с; вычислено сервером',
    'rep.podpis.cache_updated_ago': 'запись обновлена ~{age} назад',
    'rep.podpis.in_process_only': 'ответ собран из памяти процесса (storage: in_process_only)',
    'rep.podpis.express_run': 'экспресс-прогон: один повтор на подсказку вместо трёх — точность ниже',
    'rep.podpis.time_s': '{n} с',
    'rep.podpis.time_m': '{n} мин',
    'rep.podpis.time_h': '{n} ч',
    'rep.podpis.time_d': '{n} сут',

    // ── Полоса шкалы (Polosa) ────────────────────────────────────────────────
    'rep.bar.no_value': 'величины в ответе нет',
    'rep.bar.no_denominator': 'доли нет: знаменатель ноль — делить не на что',
    'rep.bar.measured_zero': 'измеренный ноль',
    'rep.bar.over_100': 'полоса упёрлась в 100: величина из ответа больше шкалы (показана как есть)',
    'rep.bar.under_0': 'полоса на нуле: величина из ответа меньше шкалы (показана как есть)',

    // ── Запасные тексты и причины (Fallbacks) ─────────────────────────────────
    'rep.fallback.no_source_long': 'Источника нет: эта величина не измеряется — ручки, которая бы её выдала, не существует.',
    'rep.fallback.db_read_error': 'сбой чтения базы данных',
    'rep.fallback.reason_not_stated': 'причина не названа',
    'rep.fallback.no_profiles': 'Профилей нет: открывать нечего. Список профилей приходит пропом profiles.',
    'rep.fallback.profile_not_linked': 'Профиль не привязан',

    // ── Названия каналов (Channel Titles) ─────────────────────────────────────
    'rep.channel_title.voice': 'Voice AI (Sofia)',
    'rep.channel_title.email': 'Email (Sofia AI)',
    'rep.channel_title.form': 'Форма на сайте',
    'rep.channel_title.chat': 'Чат сайта (Sofia)',
    'rep.channel_title.maps': 'Google Maps',
    'rep.channel_title.referrals': 'Рекомендации и сеть адвокатов',
    'rep.channel_title.calendar': 'Виджет календаря',
    'rep.channel_title.other': 'Другие',
    // Каналы ELN (data/features/eln.json → marketing_channels[].id)
    'rep.channel_title.form_contact': 'Форма на сайте',
    'rep.channel_title.chat_widget': 'Чат-виджет',
    'rep.channel_title.widget_calendar': 'Виджет календаря',
    'rep.channel_title.web_chat': 'Чат сайта (Sofia)',

    // ── Стратегический план GMB (Strategic Plan) ─────────────────────────────
    'rep.plan.title': 'Приоритет: локальная видимость {office} · «{keyword}»',
    'rep.plan.impact_not_evaluated': 'Влияние не оценивалось',
    'rep.plan.hypothesis_badge': '[Гипотеза, не проверялось]',
    'rep.plan.hypothesis_desc': 'Снижение видимости за пределами центрального сектора офиса может быть связано с плотностью конкурентов и недостатком локальных цитат с привязкой к конкретному городу.',
    'rep.plan.office_query': 'Офис и запрос:',
    'rep.plan.grid_positions': 'Позиции в сетке:',
    'rep.plan.top3_count': 'в топ-3: {n} из {m} ({pct}%)',
    'rep.plan.top3_share': 'доля в топ-3: {pct}%',
    'rep.plan.top10_count': 'в топ-10: {n} из {m} ({pct}%)',
    'rep.plan.top10_share': 'доля в топ-10: {pct}%',
    'rep.plan.top10_not_measured': 'в топ-10: не измерено',
    'rep.plan.coverage_label': 'Охват выдачи:',
    'rep.plan.found_in_points': 'найден в {found} из {total} точек',
    'rep.plan.found_simple': 'найден в {found} точках',
    'rep.plan.not_found_points': '(не найден в {n})',
    'rep.plan.leaders_label': 'Лидеры выдачи:',
    'rep.plan.recommendation': 'Рекомендация: точечное усиление локального цитирования в секторах с рангом >3',
    'rep.plan.next_scan': 'Следующий замер: по расписанию C2, раз в 7 дней',
    'rep.plan.no_scans': 'Подтверждённых замеров геосетки пока нет. Стратегический план формируется динамически после первого цикла сканирования C2.',

    // ── Присутствие в справочниках (Directory Presence) ──────────────────────
    'rep.directory_presence.title': 'Авторитет в активных каталогах',
    'rep.directory_presence.desc': 'Записи справочников строятся из канона держателя офисов — по одной на офис, и ни одной сверх',
    'rep.directory_presence.no_check': 'проверка не проводится',
    'rep.directory_presence.unverified': 'состояние записи не проверялось; подтверждённой ссылки на запись в каноне нет',

    // ── Распределение сигналов (Signal Distribution) ─────────────────────────
    'rep.signals.title': 'Распределение сигналов',

    // ── Журнал аудитов и замеров (Audit & Scan History) ───────────────────────
    'rep.audit_history.title': 'История аудитов и замеров',
    'rep.audit_history.desc': 'Журнал подтверждённых замеров геосетки робота C2',
    'rep.audit_history.scan_time': 'Время замера:',
    'rep.audit_history.queries_count': 'Запросов ({n}):',
    'rep.audit_history.queries_list': 'Список запросов:',
    'rep.audit_history.source_os': 'список: OS',
    'rep.audit_history.source_config': 'список: конфиг C2',
    'rep.audit_history.source_fallback_reason': 'список: резерв ({reason})',
    'rep.audit_history.source_fallback': 'список: резерв',
    'rep.audit_history.source_not_specified': 'список: не указан',
    'rep.audit_history.google_rating': 'Рейтинг Google:',
    'rep.audit_history.grid': 'Сетка: {coverage}',
    'rep.audit_history.schedule': 'Расписание: по расписанию C2, раз в 7 дней',
    'rep.audit_history.empty': 'Подтверждённых замеров в локальном хранилище пока нет. Замеры проводятся по расписанию C2, раз в 7 дней.',
    'rep.audit_history.status_success': 'Успешно',

    // ── Профили офисов и канонические привязки ────────────────────────────────
    'rep.office.header_canon_agents': 'КАНОН ОФИСОВ И 10 AI-АГЕНТОВ',
    'rep.office.header_profiles_title': 'Профили офисов и канонические привязки',
    'rep.office.header_profiles_desc': 'Запуск битв агентов, управление реквизитами NAP и проверка замеров геосетки',
    'rep.office.strategic_plan_header': 'Стратегический план действий',
    'rep.office.priority_recommendation': 'Приоритетная рекомендация',
    'rep.office.truth_main_title': 'Источник правды главного профиля',
    'rep.office.main_canon': 'Главный офис канона.',
    'rep.office.main_canon_badge': 'Главный офис канона',
    'rep.office.branch_canon': 'Филиал канона.',
    'rep.office.branch_badge': 'Филиал',
    'rep.office.hours_notice': 'Режим приёма и часы в этом экране не показываются: их держит канон офисов, и второй копии здесь не заводится.',
    'rep.office.unlinked_profile_notice': 'Профиль не привязан: перехода в пульт GMB, арену и аналитику нет.',
    'rep.office.step_no_profile': 'Профиль в системе не создан',
    'rep.office.btn_create_profile': 'Создать профиль из канона офиса',
    'rep.office.creating_profile': 'Создание профиля...',
    'rep.office.step_no_google': 'Google не подключён',
    'rep.office.btn_connect_google': 'Подключить Google',
    'rep.office.step_no_place_id': 'Листинг не связан (place_id отсутствует в каноне)',
    'rep.office.create_profile_error': 'Ошибка создания профиля: {error}',
    'rep.office.create_profile_success': 'Профиль офиса успешно создан',
    'rep.office.connect_google_error': 'Ошибка подключения Google: {error}',
    'rep.office.truth_source_title': 'Источник правды офиса',
    'rep.office.arena_title': 'Арена',
    'rep.office.analytics_title': 'Пульт аналитики',
    'rep.office.gmb_console_title': 'Открыть пульт управления GMB',
    'rep.office.aria_label': 'Офис {name}',
    'rep.office.selected': 'выбран',
    'rep.office.visibility_not_measured': 'Видимость не измерялась',

    // ── Состояние держателя офисов (SostoyanieOfisov) ─────────────────────────
    'rep.sostoyanie_ofisov.loading': 'Офисы ещё не получены от держателя: состояние неизвестно.',
    'rep.sostoyanie_ofisov.failed': 'Офисы прочитать не удалось: {error}. Это НЕ значит, что офисов нет — значит, канон не прочитан, и показывать здесь нечего.',
    'rep.sostoyanie_ofisov.empty': 'Офисов у арендатора нет: держатель ответил пустым списком.',

    // ── Множественное число: Показы (Impressions) ────────────────────
    'rep.plural.pokazov.one': '{n} показ',
    'rep.plural.pokazov.few': '{n} показа',
    'rep.plural.pokazov.many': '{n} показов',

    // ── Маркетинг пульс тултипы и метрики ────────────────────────────────────
    'rep.pulse.tooltip_leads': '{date}: {leads}',
    'rep.pulse.tooltip_gsc': '{date}: {clicks}, {impressions}, CTR {ctr}%, поз. {position}',
    'rep.pulse.pos_prefix': 'поз.',

    // ── Геосетка UI (OfficeVisibilityBlock) ──────────────────────────────────
    'rep.geogrid_ui.service': 'Служба геосетки:',
    'rep.geogrid_ui.title': 'Видимость в Google Maps',
    'rep.geogrid_ui.configure_queries': 'Настроить запросы',
    'rep.geogrid_ui.configure_queries_title': 'Настройка поисковых запросов для этого офиса',
    'rep.geogrid_ui.not_measured': 'Не измерялось',
    'rep.geogrid_ui.top3_share': 'Доля в топ-3',
    'rep.geogrid_ui.top10_share': 'Доля в топ-10',
    'rep.geogrid_ui.scan_never_run': 'Замер видимости по геосетке для этого филиала ещё не проводился. Появится после выполнения первого сканирования роботом C2.',
    'rep.geogrid_ui.scan_heading': 'Видимость Google Maps',
    'rep.geogrid_ui.scan_title': 'Время завершения замера в поясе офиса: {date}',
    'rep.geogrid_ui.scan_badge': 'Замер {date}',
    'rep.geogrid_ui.rating_unmeasured': 'Рейтинг Google Maps: не измерено',
    'rep.geogrid_ui.will_measure_next': 'будет измерен при следующем сканировании',
    'rep.geogrid_ui.query_label': 'Запрос:',
    'rep.geogrid_ui.choose_query': 'Выберите запрос',
    'rep.geogrid_ui.search_placeholder': 'Поиск запроса...',
    'rep.geogrid_ui.not_measured_yet_group': 'Ещё не замерялись · появятся после замера',
    'rep.geogrid_ui.nothing_found': 'Ничего не найдено',
    'rep.geogrid_ui.query_unmeasured_notice': 'Этот запрос добавлен в настройках и ещё не замерялся. Появится после следующего сканирования C2.',
    'rep.geogrid_ui.grid_title': 'Геосетка {rows}×{cols} ({size}×{size} км · шаг ~{step} км · С ↑)',
    'rep.geogrid_ui.office_pin': 'Офис',
    'rep.geogrid_ui.north_up': 'С ↑',
    'rep.geogrid_ui.north_up_title': 'Ориентация: Север вверху',
    'rep.geogrid_ui.in_top_3': 'в топ-3: {val}',
    'rep.geogrid_ui.found_in_top_20': 'найдена в топ-20: {val}',
    'rep.geogrid_ui.in_top_10': 'в топ-10: {val}',
    'rep.geogrid_ui.radius_reach': 'охват в радиусе {radius} км',
    'rep.geogrid_ui.radius_unmeasured': 'охват: не измерено',
    'rep.geogrid_ui.scan_coverage': 'Покрытие замера:',
    'rep.geogrid_ui.quality_gate_failed': 'Замер неполный: порог качества 95% не достигнут (покрытие {coverage})',
    'rep.geogrid_ui.search_leaders': 'Лидеры в локальном поиске',
    'rep.geogrid_ui.our_office': '(наш офис)',
    'rep.geogrid_ui.competitors_not_found': 'Соперники в топ-3 не найдены',
    'rep.geogrid_ui.disclaimer_title': 'Что эти числа не доказывают',
    'rep.geogrid_ui.disclaimer_1_title': 'Не доказывают звонки и обращения:',
    'rep.geogrid_ui.disclaimer_1_text': 'высокая позиция в поиске даёт показы, но не гарантирует звонков и консультаций.',
    'rep.geogrid_ui.disclaimer_2_title': 'Не учитывают персонализацию:',
    'rep.geogrid_ui.disclaimer_2_text': 'поисковая выдача реального пользователя зависит от его истории, устройства и точной геолокации.',
    'rep.geogrid_ui.disclaimer_3_title': 'Отражают мгновенный срез:',
    'rep.geogrid_ui.disclaimer_3_text': 'позиции зафиксированы в момент сканирования в радиусе вокруг координат офиса, а не навсегда.',

    // ── Модальные окна (rep.modal.*) ──────────────────────────────────────────
    // FullAuditModal
    'rep.modal.audit.title': 'Полный аудит репутации',
    'rep.modal.audit.subtitle': 'Ход показывается по событиям сервера: полоса двигается, когда этап завершён, и никак иначе.',
    'rep.modal.audit.close': 'Закрыть',
    'rep.modal.audit.btn_running': 'Прогон идёт',
    'rep.modal.audit.btn_run': 'Запустить аудит',
    'rep.modal.audit.disclaimer': 'Прогон тратит платные ресурсы: обращение к платному Ahrefs и опрос нейросетей — по одному провайдеру на каждый заданный ключ. Сколько именно вызовов и units уйдёт — здесь не известно: число подсказок и повторов задаётся на сервере, а расход units мы не измеряли.',
    'rep.modal.audit.progress': 'Ход',
    'rep.modal.audit.stage.nap': 'Согласованность NAP в каталогах',
    'rep.modal.audit.stage.ahrefs': 'Метрики домена (Ahrefs)',
    'rep.modal.audit.stage.geo': 'Опрос нейросетей',
    'rep.modal.audit.stage.prescriptions': 'Сбор предписаний',
    'rep.modal.audit.ishod.pending': 'ожидает',
    'rep.modal.audit.ishod.running': 'идёт',
    'rep.modal.audit.ishod.ok': 'готово',
    'rep.modal.audit.ishod.not_configured': 'не спрашивали',
    'rep.modal.audit.ishod.failed': 'отказ',
    'rep.modal.audit.ishod.not_measured': 'не измерено',
    'rep.modal.audit.nap_not_measured': 'Не измерено — канон NAP не заведён',
    'rep.modal.audit.btn_run_again': 'Запустить снова',
    'rep.modal.audit.reason.missing_key': 'Не задан ключ API ({key})',
    'rep.modal.audit.reason.missing_model': 'Не задана модель ({model_var})',
    'rep.modal.audit.reason.domain_not_set': 'Домен компании не задан в настройках',
    'rep.modal.audit.reason.empty_response': 'Получен пустой ответ от API',
    'rep.modal.audit.reason.prompts_missing': 'Файл конфигурации подсказок не найден для арендатора',
    'rep.modal.audit.reason.nap_canon_missing': 'Файл канона NAP не найден',
    'rep.modal.audit.reason.nap_canon_invalid': 'Канон NAP неполный или повреждён',
    'rep.modal.audit.reason.none_configured': 'Ни один поисковый провайдер не настроен',
    'rep.modal.audit.reason.all_failed': 'Отказ всех поисковых провайдеров',
    'rep.modal.audit.reason.no_nap_summary': 'Аудит NAP не проведён — предписания недоступны',
    'rep.modal.audit.reason.run_not_found': 'Прогон не найден в памяти процесса (сбрасывается при перезапуске)',
    'rep.modal.audit.reason.provider_error': 'Ошибка провайдера ({error})',
    'rep.modal.audit.reason.unexpected_error': 'Неожиданная ошибка ({error})',
    'rep.modal.audit.directories': 'Каталоги',
    'rep.modal.audit.nap_summary': 'Измерено {measured} из {total} · индекс {index}',
    'rep.modal.audit.nap_method': 'метод замера: человек, снимки экрана · {date}',
    'rep.modal.audit.domain_metrics': 'Метрики домена',
    'rep.modal.audit.ahrefs_missing_key': 'API Key missing — {detail}. Ни одного значения не показано: не измеряли.',
    'rep.modal.audit.hidden_nested': 'вложенных полей не показано: {count}',
    'rep.modal.audit.measured_by': 'чем измерено: {source}',
    'rep.modal.audit.target_domain': 'по домену: {target}',
    'rep.modal.audit.measured_at': 'момент замера: {date}',
    'rep.modal.audit.snapshot_for': 'снимок за {date}',
    'rep.modal.audit.clock': 'часы',
    'rep.modal.audit.no_country_filter': 'страновой фильтр не передавался: числа не по стране',
    'rep.modal.audit.country_filter': 'страновой фильтр: {country}',
    'rep.modal.audit.no_values': 'значений нет',
    'rep.modal.audit.neural_networks': 'Нейросети',
    'rep.modal.audit.express_mode': 'экспресс-прогон: один повтор на подсказку вместо трёх — точность ниже, с полным замером напрямую не сравнивать',
    'rep.modal.audit.geo_not_queried': 'не спрашивали — {detail}',
    'rep.modal.audit.geo_stats': 'подсказок опрошено: {done} · наших {our} из {total} цитат · домен не определён: {unknown}',
    'rep.modal.audit.geo_failed': 'отказ: {detail}',
    'rep.modal.audit.geo_total': 'Итого: наших {our} из {total} цитат (домен не определён: {unknown})',
    'rep.modal.audit.geo_total_desc': 'ссылки на домен фирмы среди всех цитат, собранных у опрошенных провайдеров',
    'rep.modal.audit.geo_no_summary': 'нейросети не опрашивались — свода нет',
    'rep.modal.audit.what_to_fix': 'Что чинить ({count})',
    'rep.modal.audit.open_link': 'Открыть',
    'rep.modal.audit.storage_disclaimer': 'Отчёт хранится в памяти процесса и не переживёт перезапуск бэкенда (storage: in_process_only).',
    'rep.modal.audit.err_not_started': 'Аудит не запустился',
    'rep.modal.audit.err_no_task_id': 'Сервер не вернул идентификатор прогона',
    'rep.modal.audit.err_status_failed': 'Опрос состояния не удался',

    // GeogridKeywordsModal
    'rep.modal.geogrid.title': 'Настройка поисковых запросов',
    'rep.modal.geogrid.branch': 'Филиал: {name}',
    'rep.modal.geogrid.key': 'Ключ: {kluch}',
    'rep.modal.geogrid.close': 'Закрыть',
    'rep.modal.geogrid.loading': 'Загрузка настроек ключевых слов...',
    'rep.modal.geogrid.source_label': 'Источник запросов:',
    'rep.modal.geogrid.source_os': 'Задано в OS',
    'rep.modal.geogrid.source_c2': 'Канонический конфиг C2',
    'rep.modal.geogrid.author': 'Автор: {user}',
    'rep.modal.geogrid.budget_title': 'Объём замера (49 точек / запрос)',
    'rep.modal.geogrid.queries_count': '{count} / {max} запросов',
    'rep.modal.geogrid.limit_not_reported': 'Лимит не сообщён',
    'rep.modal.geogrid.single_scan': 'Один замер',
    'rep.modal.geogrid.points_calc': 'запросов: {count} × 49 точек',
    'rep.modal.geogrid.points_value': 'точек: {points}',
    'rep.modal.geogrid.per_month': 'В месяц (4 цикла)',
    'rep.modal.geogrid.weekly_scan': 'еженедельный скан',
    'rep.modal.geogrid.abort_title': 'Предыдущий замер прерван: {reason}',
    'rep.modal.geogrid.abort_desc': 'Запланировано {planned} обращений при лимите {limit}. Сократите количество запросов до {max} и сохраните настройки.',
    'rep.modal.geogrid.query_list_title': 'Список запросов для замера:',
    'rep.modal.geogrid.min_max': 'минимум 1, максимум {max}',
    'rep.modal.geogrid.unknown_list': 'Нынешний список неизвестен — C2 ещё не сообщал',
    'rep.modal.geogrid.empty_list': 'Список пуст. Добавьте запросы ниже, чтобы настроить индивидуальный список.',
    'rep.modal.geogrid.delete_kw': 'Удалить {kw}',
    'rep.modal.geogrid.delete_query': 'Удалить запрос',
    'rep.modal.geogrid.add_query_title': 'Добавить новый запрос:',
    'rep.modal.geogrid.placeholder': 'Например: probate lawyer manhattan',
    'rep.modal.geogrid.btn_add': 'Добавить',
    'rep.modal.geogrid.schedule_title': 'Как применяются изменения:',
    'rep.modal.geogrid.schedule_desc': 'Сохранённый список передаётся в C2. Робот C2 выполнит замер по новым запросам в ближайший еженедельный замер (через 7 дней после предыдущего, в 07:15 по Нью-Йорку) либо при ручном запуске. На экране видимости новые запросы появятся сразу после окончания замера.',
    'rep.modal.geogrid.btn_saving': 'Сохранение...',
    'rep.modal.geogrid.btn_save': 'Сохранить в OS',
    'rep.modal.geogrid.err_load': 'Ошибка загрузки настроек',
    'rep.modal.geogrid.err_save': 'Ошибка сохранения настроек',
    'rep.modal.geogrid.err_limit_not_reported': 'Лимит запросов не сообщён роботом C2',
    'rep.modal.geogrid.err_min_chars': 'Запрос должен содержать не менее 2 символов',
    'rep.modal.geogrid.err_max_chars': 'Запрос должен содержать не более 200 символов',
    'rep.modal.geogrid.err_max_queries': 'Максимум {max} запросов на один офис (лимит бюджета C2)',
    'rep.modal.geogrid.err_duplicate': 'Этот запрос уже добавлен в список',
    'rep.modal.geogrid.err_at_least_one': 'Офис должен содержать как минимум один поисковый запрос',
    'rep.modal.geogrid.err_save_blocked': 'Лимит запросов не сообщён роботом C2. Сохранение заблокировано.',
    'rep.modal.geogrid.err_add_before_save': 'Добавьте хотя бы один поисковый запрос перед сохранением',
    'rep.modal.geogrid.err_max_allowed': 'Максимально допустимо {max} запросов для этого офиса',
    'rep.modal.geogrid.success_saved': 'Настройки сохранены в OS',

    // CitationsFixPacket
    'rep.modal.packet.title': 'Пакет правок по площадкам',
    'rep.modal.packet.empty': 'Данные получены, предписаний в них нет — править нечего. Ноль карточек не означает «на площадках всё верно»: пакет показывает ровно то, что нашёл аудит, а чего аудит не наблюдал, того здесь нет.',
    'rep.modal.packet.stats': 'Площадок в пакете: {ploshchadki} · предписаний в пакете: {vPakete} · вне пакета: {vnePaketa} · требуют решения человека: {trebuyutResheniya}',
    'rep.modal.packet.canon_no_value': ' · канон не даёт значения: {count}',
    'rep.modal.packet.no_write_disclaimer': '🔴 Правок на чужих площадках отсюда не делается: ручек записи нет ни к одной. Каждый заход человек выполняет руками в интерфейсе площадки.',
    'rep.modal.packet.ordering_disclaimer': 'Порядок площадок — тот, в котором их отдала ручка: первой стоит площадка-первоисточник, из которой агрегаторы снимают данные. Это ПРЕДПОЛОЖЕНИЕ о поведении чужих систем, пришедшее от внешней проверки, а не измерение.',
    'rep.modal.packet.prescriptions_count': 'предписаний: {count}',
    'rep.modal.packet.field': 'Поле: {field}',
    'rep.modal.packet.source': ' · источник: {role}',
    'rep.modal.packet.btn_copy': 'Скопировать',
    'rep.modal.packet.btn_copied': 'скопировано',
    'rep.modal.packet.btn_copy_err': 'не скопировалось: {err}',
    'rep.modal.packet.clipboard_unavailable': 'буфер обмена недоступен в этой среде',
    'rep.modal.packet.remove_title': 'Убрать из профиля',
    'rep.modal.packet.remove_disclaimer': 'Значения для вставки здесь нет по природе: это запрет на публикацию, а не замена одного текста другим.',
    'rep.modal.packet.decision_title': 'Решение принимает человек — готового значения нет',
    'rep.modal.packet.consequence': 'Последствие: {text}',
    'rep.modal.packet.street_sab': ' · улица не публикуется (SAB) — адрес к переносу отсюда не берётся',
    'rep.modal.packet.canon_no_options': 'Вариантов канон не перечисляет — что именно подставить, решает человек.',
    'rep.modal.packet.decision_disclaimer': 'Выбор делается непосредственно перед сохранением формы площадки. Откат на чужой площадке идёт через её поддержку, а не правкой.',
    'rep.modal.packet.unknown_title': 'Канон не даёт значения — остановиться и спросить',
    'rep.modal.packet.unknown_disclaimer': 'Правдоподобное значение хуже пустоты: пустоту видно, выдумку — нет.',
    'rep.modal.packet.outside_title': 'Вне пакета — действий нет',
    'rep.modal.packet.await_disclaimer': ' — повторная подача при рассматриваемой заявке вредна',
    'rep.modal.packet.fields.name': 'Название фирмы',
    'rep.modal.packet.fields.address': 'Адрес',
    'rep.modal.packet.fields.street': 'Улица',
    'rep.modal.packet.fields.suite': 'Офис / юнит',
    'rep.modal.packet.fields.city_state_zip': 'Город, штат, индекс',
    'rep.modal.packet.fields.phone': 'Телефон',
    'rep.modal.packet.fields.website': 'Сайт',
    'rep.modal.packet.fields.attorney': 'Адвокат',
    'rep.modal.packet.fields.bar_number': 'Номер Bar',
    'rep.modal.packet.roles.MAIN': 'главный офис',
    'rep.modal.packet.roles.BRANCH': 'филиал',
    'rep.modal.packet.variants.city_only.title': 'Оставить только город (приватность)',
    'rep.modal.packet.variants.city_only.desc': 'адреса здания на площадке сейчас нет; канон разрешает для текстовых каталогов уровень города',
    'rep.modal.packet.variants.street_parity.title': 'Указать улицу без квартиры (паритет NAP)',
    'rep.modal.packet.variants.street_parity.desc': 'этот адрес уже виден в карточке Google, реестре коллегии и части каталогов — тайной не является; единый полный адрес усиливает вес профиля',
    'rep.modal.packet.variants.keep_branch.title': 'Оставить филиал, убрав только запрещённый фрагмент',
    'rep.modal.packet.variants.keep_branch.desc': 'преемственность с профилями и отзывами, привязанными к филиалу',
    'rep.modal.packet.variants.move_main.title': 'Перевести на главный офис канона',
    'rep.modal.packet.variants.move_main.desc': 'реестр коллегии указывает на главный физический офис; потребует перевода остальных одноместных листингов туда же',
    'rep.modal.packet.razvilki.single_location': 'Площадка одноместная, а профиль заведён на филиал — какую локацию оставить',
    'rep.modal.packet.razvilki.street_absent': 'На площадке улицы нет вовсе — публиковать ли её здесь впервые',

    // GmbCommandModal
    'rep.modal.gmb.title': 'Пульт управления Google Business Profile',
    'rep.modal.gmb.main_canon': 'Главный канон',
    'rep.modal.gmb.branch': 'Филиал',
    'rep.modal.gmb.synced_at': 'Синхронизировано: {date}',
    'rep.modal.gmb.not_synced': 'Синхронизации не было',
    'rep.modal.gmb.btn_syncing': 'Синхронизация...',
    'rep.modal.gmb.btn_sync_now': 'Синхронизировать сейчас',
    'rep.modal.gmb.btn_open_maps': 'Открыть Maps',
    'rep.modal.gmb.tab_audit': 'Аудит 7 факторов',
    'rep.modal.gmb.tab_posts': 'Автопилот публикаций Google',
    'rep.modal.gmb.tab_reviews': 'Отзывы и ответы AI',
    'rep.modal.gmb.tab_services': 'Каталог услуг',
    'rep.modal.gmb.tab_autopilot': 'Настройки автопилота 24ч',
    'rep.modal.gmb.not_connected_warning': 'Google Business Profile не подключён. Данные о просмотрах и звонках станут доступны после авторизации в Google My Business.',
    'rep.modal.gmb.status.unknown_query_failed': 'Состояние записи НЕИЗВЕСТНО: спросить не удалось.',
    'rep.modal.gmb.status.failure_reason': 'Причина отказа: {reason}. Ни одно число ниже не подставлено вместо ответа — их просто нет. {warning}',
    'rep.modal.gmb.status.pending_response': 'Ответ ручки ещё не получен: состояние записи не показывается, потому что оно неизвестно.',
    'rep.modal.gmb.status.sync_error': 'Последняя синхронизация завершилась ошибкой.',
    'rep.modal.gmb.status.reason_not_specified': 'причина не названа',
    'rep.modal.gmb.status.synced': 'Запись синхронизирована.',
    'rep.modal.gmb.status.sync_moment': 'Момент синхронизации (по серверу): {date}',
    'rep.modal.gmb.status.not_connected_state': 'Состояние записи: NOT_CONNECTED.',
    'rep.modal.gmb.status.unrecognized': 'Состояние записи не опознано: {status}.',
    'rep.modal.gmb.status.known_values': 'Известные значения: NOT_CONNECTED, synced, pending, error.',
    'rep.modal.gmb.metrics_30d_title': 'Показатели Google за 30 дней',
    'rep.modal.gmb.metrics_source_hint': 'источник — ответ ручки; прочерк означает «замера не было»',
    'rep.modal.gmb.metric.views': 'Просмотры',
    'rep.modal.gmb.metric.searches': 'Поиски',
    'rep.modal.gmb.metric.calls': 'Звонки',
    'rep.modal.gmb.metric.website_clicks': 'Переходы на сайт',
    'rep.modal.gmb.metric.directions': 'Маршруты',
    'rep.modal.gmb.metric.rating': 'Рейтинг',
    'rep.modal.gmb.metric.reviews': 'Отзывов',
    'rep.modal.gmb.metric.audit_score': 'Оценка аудита',
    'rep.modal.gmb.tooltip.syncing': 'Идёт обращение к ручке: подождите ответа.',
    'rep.modal.gmb.tooltip.no_account': 'Требуется подключение аккаунта Google',
    'rep.modal.gmb.tooltip.no_scope': 'Требуется повторная авторизация в Google с правами управления Business Profile',
    'rep.modal.gmb.tooltip.no_can_sync': 'Сервер не подтвердил готовность к синхронизации: признак can_sync не получен',
    'rep.modal.gmb.tooltip.server_prefix': '{hint}. Сервер: {msg}',
    'rep.modal.gmb.tooltip.status_unknown': 'Состояние записи неизвестно: ручка не ответила, спрашивать нечего.',
    'rep.modal.gmb.tooltip.not_connected': 'Запись не подключена к Google: спрашивать нечего.',
    'rep.modal.gmb.tooltip.ready': 'Спросить Google и обновить отметку синхронизации.',
    'rep.modal.gmb.toast.sync_ok': 'Google ответил: отметка синхронизации обновлена.',
    'rep.modal.gmb.toast.sync_skipped': 'Синхронизации с Google не было: {reason}. Ниже показано МЕСТНОЕ состояние записи, а не ответ Google.',
    'rep.modal.gmb.toast.sync_failed': 'Синхронизация НЕ выполнена: {reason}',
    'rep.modal.gmb.toast.audit_ok': 'Аудит выполнен. Оценка: {score}',
    'rep.modal.gmb.toast.audit_failed': 'Аудит НЕ выполнен: {reason}',
    'rep.modal.gmb.toast.google_auth_opened': 'Авторизация Google открыта в новой вкладке. Подтвердите и вернитесь.',
    'rep.modal.gmb.toast.google_auth_failed': 'Не удалось запустить подключение к Google',
    'rep.modal.gmb.toast.text_copied_open_google': '📋 Текст скопирован! Открываем профиль в Google — нажмите "+ Add update" и вставьте текст (Cmd+V).',
    'rep.modal.gmb.toast.post_content_empty': 'Сначала сгенерируйте или введите текст публикации',
    'rep.modal.gmb.toast.draft_generated': 'Черновик публикации сгенерирован в Gemini 2.5',
    'rep.modal.gmb.toast.draft_failed': 'Черновик НЕ создан: {reason}',
    'rep.modal.gmb.toast.post_empty': 'Текст публикации не может быть пустым',
    'rep.modal.gmb.toast.post_live': 'Публикация успешно размещена на Google Maps!',
    'rep.modal.gmb.toast.post_copied_open': '📋 Текст поста скопирован в буфер! Откройте карточку в Google кнопкой ниже и вставьте (Cmd+V).',
    'rep.modal.gmb.toast.post_failed': 'Пост НЕ сохранён: {reason}',
    'rep.modal.gmb.toast.post_marked_live': 'Пост подтвержден как опубликованный на Google Maps!',
    'rep.modal.gmb.toast.mark_failed': 'Отметку не удалось сохранить: {reason}',
    'rep.modal.gmb.toast.reply_draft_failed': 'Черновик ответа НЕ создан: {reason}',
    'rep.modal.gmb.toast.reply_live': 'Ответ адвоката опубликован на Google Maps!',
    'rep.modal.gmb.toast.reply_failed': 'Ответ НЕ опубликован: {reason}',
    'rep.modal.gmb.toast.autopilot_saved': 'Настройки автопилота сохранены ручкой.',
    'rep.modal.gmb.toast.autopilot_failed': 'Настройки автопилота НЕ сохранены: {reason}',
    'rep.modal.gmb.toast.photos_max_50': 'Можно загружать до 50 фото одновременно.',
    'rep.modal.gmb.toast.photos_uploaded': 'Успешно загружено {count} фото адвоката в медиа-хранилище!',
    'rep.modal.gmb.toast.photos_error': 'Ошибка при загрузке фото',
    'rep.modal.gmb.toast.random_photo_picked': 'Выбрано случайное фото адвоката!',
    'rep.modal.gmb.toast.services_not_pushed': 'Отсюда услуги в Google не отправляются: ручки выгрузки каталога у этого экрана нет.',
    'rep.modal.gmb.audit.no_measurement': 'замера нет',
    'rep.modal.gmb.audit.measured_at': 'Замер: {date}',
    'rep.modal.gmb.audit.time_unknown': 'Момент замера неизвестен',
    'rep.modal.gmb.audit.audited_by_model': 'Аудит моделью: {model}',
    'rep.modal.gmb.audit.model_not_named': 'модель не названа',
    'rep.modal.gmb.audit.health_score_title': 'Индекс здоровья оптимизации профиля',
    'rep.modal.gmb.audit.no_audit_summary': 'Аудит не проводился: сводки нет.',
    'rep.modal.gmb.audit.btn_repeat': 'Повторить глубокий аудит',
    'rep.modal.gmb.audit.repeat_disabled_hint': 'аудит переделывается: оценки будут считаться из замеров',
    'rep.modal.gmb.audit.factors_title': '7 ключевых факторов локального ранжирования',
    'rep.modal.gmb.audit.factor.nap_consistency': 'Согласие адреса, телефона и реестровой записи',
    'rep.modal.gmb.audit.factor.categories': 'Основная и дополнительные категории записи',
    'rep.modal.gmb.audit.factor.services_catalog': 'Заполненность каталога услуг',
    'rep.modal.gmb.audit.factor.visual_media': 'Фотографии записи',
    'rep.modal.gmb.audit.factor.posts_cadence': 'Регулярность публикаций',
    'rep.modal.gmb.audit.factor.review_velocity': 'Скорость набора отзывов и ответы на них',
    'rep.modal.gmb.audit.factor.local_prominence': 'Заметность записи в локальной выдаче',
    'rep.modal.gmb.audit.fixes_title': 'Рекомендации AI по улучшению и пробелы',
    'rep.modal.gmb.posts.api_connected': 'Google Business Profile API: подключено и авторизовано',
    'rep.modal.gmb.posts.api_status': 'Google Business Profile API: статус подключения',
    'rep.modal.gmb.posts.api_authorized_for': 'Авторизовано для {email}. Публикации отправляются напрямую в Google Maps.',
    'rep.modal.gmb.posts.api_link_hint': 'Подключите {email} с правами Business Profile для публикации в 1 клик, или используйте 📋 Скопировать и открыть.',
    'rep.modal.gmb.posts.btn_connecting': 'Подключение...',
    'rep.modal.gmb.posts.btn_connect_auto': '🔑 Подключить авто-публикацию в Google',
    'rep.modal.gmb.posts.btn_open_profile': 'Открыть профиль в Google',
    'rep.modal.gmb.posts.create_title': 'Создать и опубликовать запись в Google',
    'rep.modal.gmb.posts.create_subtitle': 'Публикуется напрямую в Google Maps для {firm} в г. {city}.',
    'rep.modal.gmb.posts.btn_generating': 'Генерация...',
    'rep.modal.gmb.posts.btn_generate': '✨ Сгенерировать через Gemini',
    'rep.modal.gmb.posts.topic_label': 'Юридическая тема или инфоповод (опционально)',
    'rep.modal.gmb.posts.topic_placeholder': 'например, Сроки наследственного процесса в Нью-Йорке, Завещание или отзывной траст, Консультации по наследственному планированию',
    'rep.modal.gmb.posts.suggested_label': 'Рекомендуемые:',
    'rep.modal.gmb.posts.content_label': 'Текст публикации (до 1500 символов)',
    'rep.modal.gmb.posts.content_placeholder': 'Введите текст публикации или нажмите «Сгенерировать через Gemini» выше...',
    'rep.modal.gmb.posts.media_label': 'Фото адвоката или офиса (медиафайл для публикации в Google Maps)',
    'rep.modal.gmb.posts.btn_uploading': 'Загрузка...',
    'rep.modal.gmb.posts.btn_upload_bulk': '+ Загрузить пачку фото (до 50)',
    'rep.modal.gmb.posts.btn_collapse_gallery': 'Свернуть галерею',
    'rep.modal.gmb.posts.btn_choose_gallery': 'Выбрать из галереи ({count})',
    'rep.modal.gmb.posts.will_attach_hint': 'Будет прикреплено к посту в Google Maps',
    'rep.modal.gmb.posts.btn_random_photo': '🎲 Случайное',
    'rep.modal.gmb.posts.btn_remove_photo': 'Убрать фото',
    'rep.modal.gmb.posts.no_photo_attached': 'Фото не прикреплено. Нажмите',
    'rep.modal.gmb.posts.choose_from_gallery_link': 'выбрать из галереи',
    'rep.modal.gmb.posts.or_upload_new': 'или загрузите новые фото адвоката.',
    'rep.modal.gmb.posts.media_library_title': 'Медиа-библиотека адвоката ({count} фото)',
    'rep.modal.gmb.posts.category_all': 'Все',
    'rep.modal.gmb.posts.cta_button_label': 'Кнопка действия (CTA)',
    'rep.modal.gmb.posts.cta_book': 'Записаться на приём',
    'rep.modal.gmb.posts.cta_call': 'Позвонить сейчас',
    'rep.modal.gmb.posts.cta_learn_more': 'Подробнее',
    'rep.modal.gmb.posts.cta_url_label': 'Целевая ссылка для кнопки',
    'rep.modal.gmb.posts.btn_copy_open': '📋 Скопировать и открыть в Google',
    'rep.modal.gmb.posts.btn_publishing': 'Публикация...',
    'rep.modal.gmb.posts.btn_publish': 'Опубликовать в Google Maps',
    'rep.modal.gmb.posts.recent_title': 'Недавние публикации в Google ({count})',
    'rep.modal.gmb.posts.badge_live': 'ОПУБЛИКОВАНО В GOOGLE MAPS',
    'rep.modal.gmb.posts.badge_saved_os': 'НЕ ОПУБЛИКОВАНО В GOOGLE MAPS (СОХРАНЕНО В OS)',
    'rep.modal.gmb.posts.saved_os_hint': '⚠️ Сохранено в OS. Нажмите, чтобы скопировать и вставить в Google:',
    'rep.modal.gmb.posts.btn_auto_pub': '🔑 Авто-публикация',
    'rep.modal.gmb.posts.btn_published_mark': '✓ Опубликовано',
    'rep.modal.gmb.posts.no_posts_empty': 'Пока нет сохранённых публикаций. Нажмите «Сгенерировать через Gemini», чтобы создать первую запись недели.',
    'rep.modal.gmb.reviews.title': 'Отзывы Google Maps ({count})',
    'rep.modal.gmb.reviews.signature_hint': 'Подпись под ответом ставится только та, которую передал человек.',
    'rep.modal.gmb.reviews.coverage': '100% охват ответами',
    'rep.modal.gmb.reviews.badge_replied': 'Отвечено',
    'rep.modal.gmb.reviews.badge_pending': 'Ожидает ответа',
    'rep.modal.gmb.reviews.official_reply': 'Официальный ответ',
    'rep.modal.gmb.reviews.official_reply_by': 'Официальный ответ — {author}',
    'rep.modal.gmb.reviews.btn_publish_reply': 'Опубликовать ответ в Google',
    'rep.modal.gmb.reviews.btn_drafting': 'Подготовка...',
    'rep.modal.gmb.reviews.btn_draft_reply': '✨ Подготовить ответ адвоката через AI',
    'rep.modal.gmb.reviews.empty': 'Для этой локации пока нет отзывов Google. Новые отзывы автоматически загружаются каждые 24 часа.',
    'rep.modal.gmb.services.title': 'Каталог услуг в Google Maps',
    'rep.modal.gmb.services.subtitle': 'Канонические направления практики, опубликованные в профиле Google Business Profile.',
    'rep.modal.gmb.services.btn_push': 'Отправить услуги в GMB',
    'rep.modal.gmb.services.default_category': 'Иммиграционная практика',
    'rep.modal.gmb.services.verified': 'Верифицировано',
    'rep.modal.gmb.autopilot.title': 'Настройки круглосуточной автономии GMB',
    'rep.modal.gmb.autopilot.subtitle': 'Настройка фоновой автоматизации Celery Beat, еженедельных публикаций и автоответов на отзывы.',
    'rep.modal.gmb.autopilot.sync_title': 'Круглосуточная полная синхронизация',
    'rep.modal.gmb.autopilot.sync_desc': 'Загрузка новых отзывов, обновление рейтинга и аудит 7 факторов каждую ночь в 03:00 UTC.',
    'rep.modal.gmb.autopilot.posts_title': 'Автопилот еженедельных публикаций Google',
    'rep.modal.gmb.autopilot.posts_desc': 'Создаёт и публикует еженедельные материалы о наследственном планировании и наследственных делах с приглашением на консультацию, если за 7 дней не было постов.',
    'rep.modal.gmb.autopilot.reviews_title': 'Автоответ на отзывы 5 звёзд',
    'rep.modal.gmb.autopilot.reviews_desc': 'Автоматически публикует тёплые персонализированные ответы адвоката на все входящие отзывы 5★.',
    'rep.modal.gmb.autopilot.btn_save': 'Сохранить конфигурацию автопилота',

    // ── Новые ключи (GrowthWorkspace & bilingual) ─────────────
    'rep.error.db_read': 'сбой чтения базы данных',
    'rep.error.db_read_unmeasured': 'не измерено: сбой чтения базы данных',
    'rep.error.reason_not_specified': 'причина не названа',
    'rep.auth.connected_verified': 'Google Business Profile подключён: право на публикацию подтверждено ручкой состояния.',
    'rep.auth.connected_no_scope': 'Google подключён, но права на публикацию в Business Profile ручка НЕ подтвердила.',
    'rep.auth.not_confirmed': 'Вернулись из Google, но ручка состояния подключения НЕ подтвердила. Считайте, что не подключено.',
    'rep.auth.verify_failed': 'Вернулись из Google, но проверить подключение не удалось: {error}. Состояние НЕИЗВЕСТНО.',
    'rep.auth.incomplete': 'Не удалось завершить авторизацию Google.',
    'rep.auth.error_detail': 'Ошибка авторизации Google: {detail}',
    'rep.office.no_profiles': 'Профилей нет: открывать нечего. Список профилей приходит пропом `profiles`.',
    'rep.office.badge_main_canon': 'Главный офис канона',
    'rep.office.badge_branch': 'Филиал',
    'rep.office.badge_main_canon_caps': 'ГЛАВНЫЙ ОФИС КАНОНА',
    'rep.office.badge_branch_caps': 'ФИЛИАЛ',
    'rep.office.desc_main_canon': 'Главный офис канона.',
    'rep.office.desc_branch': 'Филиал канона.',
    'rep.office.profile_not_linked': 'Профиль не привязан',
    'rep.geogrid.points_count': ' ({validObs}/{totalPoints} точек)',
    'rep.geogrid.worst_keyword': '{covVal}%{pointsStr} · худший: «{keyword}»',
    'rep.state.not_specified': 'не указан',
    'rep.audit.querying': 'Спрашиваем сервер о последнем замере…',
    'rep.audit.query_failed': 'Не смогли спросить сервер: {error}. Это НЕ значит, что замера не было.',
    'rep.audit.server_timeout': 'Ответа о последнем замере нет: сервер промолчал.',
    'rep.audit.never_run': 'Замер не запускался.',
    'rep.audit.cache_unreadable': 'Последний замер есть, но его запись не читается — это НЕ «замеров не было».',
    'rep.audit.invalid_tenant': 'Отказ: идентификатор арендатора сервер не принял.',
    'rep.audit.no_report': 'Ответ пришёл без отчёта: показывать нечего.',
    'rep.pulse.loading_inquiry_data': 'Загрузка данных обращений из базы OS...',
    'rep.pulse.failed_inquiry_data': 'Не удалось получить данные обращений: {error}',
    'rep.pulse.cutoff_27_08': '27.08 отсечка',
    'rep.gsc.chart_tooltip': '{date}: {clicks}, {impressions}, CTR {ctr}%, {pos_label} {pos}',
    'rep.gsc.pos_label': 'поз.',
    'rep.locations.source_of_truth_main': 'Источник правды главного профиля',
    'rep.locations.title': 'Офисы практики и канонический эталон',
    'rep.locations.subtitle': 'Канонический эталон, биографии адвокатов, проверенные адреса и назначения агентов.',
    'rep.office.metric.squad': 'Отряд',
    'rep.office.metric.accuracy': 'Точность',
    'rep.office.metric.status': 'Статус',

    // ── Stage 1: Google Posts & Daily Verification ──────────────
    'rep.tab.gmb_posts': 'Посты Google',
    'rep.header.tab_gmb_posts_title': 'Посты Google и автопилот',
    'rep.header.tab_gmb_posts_desc': 'Публикации Google Business Profile, сторож соответствия канону и автопилот',
    'rep.gmb.daily_title': 'Вышло за сутки',
    'rep.gmb.daily_subtitle': 'Ежедневная проверка публикаций Google по карточке Manhattan Office (299 Broadway)',
    'rep.gmb.unverified_count': 'Не проверено: {count}',
    'rep.gmb.all_verified': 'Все посты проверены',
    'rep.gmb.verified_at': 'Проверено: {by}, {date}',
    'rep.gmb.not_verified': 'Ещё не проверено',
    'rep.gmb.btn_verify': 'Отметить проверенным',
    'rep.gmb.btn_verifying': 'Отметка...',
    'rep.gmb.btn_patch': 'Править в Google',
    'rep.gmb.btn_delete': 'Снять',
    'rep.gmb.btn_approve': 'Утвердить',
    'rep.gmb.btn_approving': 'Утверждение...',
    'rep.gmb.guard_passed_badge': 'Сторож: ПРОЙДЕН',
    'rep.gmb.guard_violations_badge': 'Сторож: ОТКЛОНЁН ({count})',
    'rep.gmb.status.published': 'Опубликовано (Google API)',
    'rep.gmb.status.published_manually': 'Опубликован вручную',
    'rep.gmb.status.needs_review': 'Замечания Katifi — нужно решение владельца',
    'rep.gmb.status.pending_approval': 'Ждёт утверждения',
    'rep.gmb.status.pending_rewrite': 'Правка ждёт утверждения',
    'rep.gmb.status.publish_unconfirmed': 'Публикация не подтверждена',
    'rep.gmb.status.removed_by_google': 'Снят Google',
    'rep.gmb.status.approved': 'Утверждён',
    'rep.gmb.btn_approve_rewrite': 'Утвердить правку',
    'rep.gmb.suggested_rewrite_title': 'Предложенный ИИ вариант (не опубликован):',
    'rep.gmb.status.expired': 'Истёк',
    'rep.gmb.status.rejected': 'Отклонён',
    'rep.gmb.status.failed': 'Ошибка (Отказ Google API)',
    'rep.gmb.status.draft': 'Черновик',
    'rep.gmb.filter_pending_approval': 'Ждут утверждения',
    'rep.gmb.filter_manual': 'Опубликованы вручную',
    'rep.gmb.btn_mark_manual': 'Опубликовано вручную',
    'rep.gmb.btn_copy_text': 'Скопировать текст',
    'rep.gmb.btn_copy_link': 'Скопировать ссылку',
    'rep.gmb.copied_text_success': 'Текст поста скопирован в буфер',
    'rep.gmb.copied_link_success': 'Ссылка кнопки скопирована в буфер',
    'rep.gmb.manual_modal_title': 'Отметить как опубликованное вручную',
    'rep.gmb.manual_modal_desc': 'Укажите ссылку на опубликованный пост в Google и подтвердите публикацию.',
    'rep.gmb.manual_modal_url_label': 'Ссылка на пост в Google',
    'rep.gmb.manual_modal_url_placeholder': 'https://posts.gle/... или https://maps.app.goo.gl/...',
    'rep.gmb.manual_modal_btn_confirm': 'Подтвердить публикацию',
    'rep.gmb.manual_modal_btn_cancel': 'Отмена',
    'rep.gmb.manual_toast_success': 'Пост отмечен как опубликованный вручную',
    'rep.gmb.manual_url_required': 'Ссылка на пост в Google обязательна (posts.gle, maps.app.goo.gl, google.com/maps)',
    'rep.gmb.btn_approve_and_publish': 'Утвердить',
    'rep.gmb.btn_reject': 'Отклонить',
    'rep.gmb.reject_title': 'Отклонить черновик поста',
    'rep.gmb.reject_reason_label': 'Причина отклонения',
    'rep.gmb.reject_reason_placeholder': 'Укажите причину отклонения...',
    'rep.gmb.reject_submit': 'Отклонить черновик',
    'rep.gmb.toast_rejected': 'Черновик отклонён',
    'rep.gmb.filter_all': 'Все карточки',
    'rep.gmb.filter_unverified': 'Только непроверенные',
    'rep.gmb.card_manhattan': 'Офис Manhattan (299 Broadway)',
    'rep.gmb.select_office_hint': 'Выберите офис карточки',
    'rep.gmb.select_office_placeholder': '-- Выберите офис --',
    'rep.gmb.confirm_delete': 'Снять этот пост с Google Maps? Будет выполнен вызов API для удаления публикации.',
    'rep.gmb.confirm_delete_manual': 'Пост опубликован вручную — удалите его в Google сами; OS изменит только свою запись. Продолжить?',
    'rep.gmb.manual_edit_warning': 'Пост опубликован вручную — измените его в Google сами; OS изменит только свою запись.',
    'rep.gmb.patch_title': 'Править публикацию в Google',
    'rep.gmb.patch_summary_label': 'Текст публикации',
    'rep.gmb.patch_save': 'Обновить через Google API',
    'rep.gmb.patch_cancel': 'Отмена',
    'rep.gmb.patch_saving': 'Обновление...',
    'rep.gmb.toast_verified': 'Пост отмечен как проверенный ({by})',
    'rep.gmb.toast_deleted': 'Пост снят с Google Maps',
    'rep.gmb.toast_patched': 'Пост обновлён в Google Maps',
    'rep.gmb.toast_approved': 'Пост одобрен и обработан',
    'rep.gmb.btn_rewrite': 'Переписать',
    'rep.gmb.rewrite_title': 'Переписать пост Google через AI',
    'rep.gmb.rewrite_feedback_label': 'Что нужно изменить? (Указание для модели)',
    'rep.gmb.rewrite_feedback_placeholder': 'например, сделать акцент на ведении наследственных дел или изменить тон...',
    'rep.gmb.rewrite_submitting': 'Переписывание через AI…',
    'rep.gmb.toast_rewritten': 'Пост успешно переписан и обновлен в Google',
    'rep.modal.gmb.toast.select_office': 'Выберите офис перед генерацией поста',
    'rep.gmb.empty_daily': 'За последние 7 дней публикаций не поступало.',
    'rep.gmb.posts_per_week_counter': 'Опубликовано за 7 дней: {count} из {target}',
    'rep.gmb.last_published_at': 'Последняя публикация: {date}',
    'rep.gmb.never_published': 'Публикаций пока нет',
    'rep.gmb.rhythm_warning_title': 'Предупреждение о темпе публикаций',
    'rep.gmb.rhythm_warning_msg': 'За последние 7 дней уже опубликовано {count} постов (рекомендуемый ритм: {target}). Слишком частые посты могут повлечь блокировку в Google (прецедент 23.10). Вы уверены, что хотите продолжить?',
    'rep.gmb.waiting_for_autopublish': 'Ждёт автопубликации',
    'rep.gmb.autopublish_stopped_banner': 'Автопубликация остановлена: {reason}, {date}',
    'rep.gmb.btn_resume_autopublish': 'Возобновить автопубликацию',
    'rep.gmb.resume_modal_title': 'Возобновление автопубликации',
    'rep.gmb.resume_modal_desc': 'Конвейер автопубликации был остановлен. Как владелец фирмы, укажите обязательное обоснование для снятия остановки и возобновления автоматической публикации в Google.',
    'rep.gmb.resume_comment_label': 'Обоснование владельца (обязательно)',
    'rep.gmb.resume_comment_placeholder': 'Укажите, почему безопасно возобновить автопубликацию (например, карточка в Google проверена, отказ устранён)...',
    'rep.gmb.btn_confirm_resume': 'Возобновить конвейер',
    'rep.gmb.btn_resuming': 'Возобновление…',
    'rep.gmb.toast_resumed': 'Конвейер автопубликации успешно возобновлен',
    'rep.gmb.resume_comment_required': 'Обоснование владельца обязательно для возобновления',
    'rep.gmb.next_autopublish_at': 'следующая автопубликация не раньше {date}',
    'rep.gmb.next_autopublish_ready': 'готово к автопубликации',
    'rep.gmb.next_autopublish_target_reached': 'недельный лимит исчерпан',
    'rep.gmb.next_autopublish_stopped': 'автопубликация остановлена',
    'rep.gmb.reason_google_rejected': 'Google отклонил публикацию',
    'rep.gmb.reason_three_consecutive_failures': '3 сбоя моста подряд',
    'rep.gmb.reason_publish_unconfirmed': 'Публикация не подтверждена после отправки',
    'rep.gmb.reason_unresolved_claim': 'Неразрешённый захват моста (ожидает проверки)',
    'rep.gmb.reason_office_not_in_canon': 'Офис не найден в каноне конфигурации',
    'rep.gmb.reason_google_posting_disabled': 'Google выключил публикацию на карточке',
    'rep.gmb.google_posting_disabled_banner': 'Google выключил публикацию на карточке {date}. Возобновить можно только после возврата публикации Google.',
    'rep.gmb.btn_google_policy_removed': 'Google снял пост (нарушение правил)',
    'rep.gmb.google_policy_modal_title': 'Google снял пост (нарушение правил)',
    'rep.gmb.google_policy_modal_desc': 'Зафиксировать снятие поста Google из-за нарушения правил и выключение публикации на карточке. Это останавливает конвейер до возврата публикации Google.',
    'rep.gmb.google_notice_label': 'Текст или тема письма Google (обязательно)',
    'rep.gmb.google_notice_placeholder': "например, Estate Law New York, your post violates Google's content policy (Routing ID: DPNB)...",
    'rep.gmb.google_policy_comment_label': 'Комментарий владельца (обязательно)',
    'rep.gmb.google_policy_comment_placeholder': 'Укажите предпринятые действия или статус апелляции...',
    'rep.gmb.btn_confirm_google_policy_removed': 'Подтвердить снятие Google',
    'rep.gmb.toast_google_policy_removed': 'Пост отмечен как снятый Google. Конвейер остановлен.',
    'rep.gmb.btn_restore_google_posting': 'Google вернул публикацию',
    'rep.gmb.restore_google_modal_title': 'Подтвердить возврат публикации Google',
    'rep.gmb.restore_google_modal_desc': 'Подтверждайте только если поддержка Google или апелляция подтвердили возврат возможности публикации на карточке.',
    'rep.gmb.restore_google_comment_label': 'Дата и подтверждение от Google (обязательно)',
    'rep.gmb.restore_google_comment_placeholder': 'например, Апелляция удовлетворена 02.11.2026, проверено появление кнопки добавления записей...',
    'rep.gmb.btn_confirm_restore_google_posting': 'Снять остановку',
    'rep.gmb.toast_google_posting_restored': 'Публикация Google восстановлена. Конвейер возобновлен.',
    'rep.gmb.publish_unconfirmed_hint': 'Пост был отправлен в Google, но подтверждение не получено. Проверьте карточку в Google вручную, чтобы подтвердить или отменить публикацию.',
    'rep.gmb.unresolved_claim_title': 'Мост взял пост, результата нет — проверьте карточку в Google',
    'rep.gmb.unresolved_claim_hint': 'Мост взял пост в работу (bridge_claimed), но отчёт не поступил. Проверьте карточку в Google: если пост вышел, подтвердите ссылку; если нет — верните в утверждённые.',
    'rep.gmb.btn_scroll_to_post': 'Перейти к посту',
    'rep.gmb.btn_confirm_published': 'Подтвердить: опубликован (ссылка Google)',
    'rep.gmb.btn_revert_to_approved': 'Не опубликован (вернуть в утверждённые)',
    'rep.gmb.btn_clear_claim': 'Не выходил (снять захват)',
    'rep.gmb.confirm_pub_modal_title': 'Подтвердить публикацию в Google',
    'rep.gmb.confirm_pub_modal_desc': 'Если вы проверили карточку в Google и пост действительно вышел, введите ссылку на пост в Google и комментарий проверки.',
    'rep.gmb.confirm_pub_url_label': 'Ссылка на пост в Google (обязательно)',
    'rep.gmb.confirm_pub_comment_label': 'Комментарий проверки владельцем (обязательно)',
    'rep.gmb.confirm_pub_comment_placeholder': 'например, проверил карточку в Google вручную, пост опубликован...',
    'rep.gmb.revert_app_modal_title': 'Вернуть в утверждённые (пост не опубликован)',
    'rep.gmb.revert_app_modal_desc': 'Если пост НЕ появился в карточке Google, подтвердите его возврат в статус утверждённых. Укажите комментарий владельца.',
    'rep.gmb.revert_app_comment_label': 'Комментарий владельца (обязательно)',
    'rep.gmb.revert_app_comment_placeholder': 'например, проверил карточку в Google, поста нет...',
    'rep.gmb.clear_claim_modal_title': 'Снять захват моста',
    'rep.gmb.clear_claim_modal_desc': 'Подтвердите, что этот пост не выходил в Google Business Profile. Это снимет блокировку моста без утверждения поста.',
    'rep.gmb.toast_confirm_published': 'Публикация поста подтверждена',
    'rep.gmb.toast_revert_approved': 'Пост возвращён в статус утверждённых',
    'rep.gmb.toast_claim_cleared': 'Захват моста снят',
    'rep.gmb.btn_verify_katifi': 'Проверить с Katifi',
    'rep.gmb.btn_verifying_katifi': 'Katifi проверяет...',
    'rep.gmb.katifi_badge_pass': 'Katifi: ПРОЙДЕНО',
    'rep.gmb.katifi_badge_fail': 'Katifi: ЗАМЕЧАНИЯ ({count})',
    'rep.gmb.katifi_badge_not_reviewed': 'Katifi: Не проверено',
    'rep.gmb.katifi_override_title': 'Утвердить вопреки замечаниям Katifi',
    'rep.gmb.katifi_override_desc': 'Katifi выявила замечания к черновику. Как владелец фирмы, укажите обязательный комментарий для утверждения:',
    'rep.gmb.katifi_override_comment_label': 'Комментарий владельца',
    'rep.gmb.katifi_override_btn': 'Утвердить с комментарием',
    'rep.gmb.katifi_override_comment_required': 'Комментарий владельца обязателен',
    'rep.gmb.open_in_google_maps': 'Открыть в Google',
    'rep.gmb.create_tab_title': 'Создать / Опубликовать пост',
    'rep.gmb.select_office_label': 'Выберите карточку',
    'rep.gmb.select_language_label': 'Язык публикации',
    'rep.gmb.language_en': 'Английский (EN)',
    'rep.gmb.language_ru': 'Русский (RU)',
    'rep.gmb.select_topic_label': 'Тема (из услуг сайта)',
    'rep.gmb.btn_publish_now': 'Опубликовать сейчас',
    'rep.gmb.btn_save_draft': 'Сохранить черновик',
    'rep.gmb.btn_saving_draft': 'Сохранение черновика...',
    'rep.gmb.toast_draft_saved': 'Черновик сохранён в OS и ожидает проверки Katifi',
    'rep.gmb.view_in_google': 'Посмотреть в Google',
    'rep.gmb.posts_api_disabled_reason': 'Google API публикаций закрыт для этого проекта. Сохраните черновик для проверки Katifi и утверждения владельцем; после утверждения опубликуйте в Google вручную.',
    'rep.gmb.btn_copy_text': 'Скопировать текст',
    'rep.gmb.copy_text_success': 'Текст скопирован в буфер',
    'rep.gmb.gemini_key_missing': 'Ключ GEMINI_API_KEY не настроен',
    'rep.gmb.no_locations_hint': 'Сначала создайте профиль офиса',
    'rep.gmb.link_office_profiles': 'Профили офисов',
    'rep.pulse.unmeasured.gbp_not_measured': 'не измерено: замер показателей не проводился',
    'rep.pulse.unmeasured.gbp_performance_failed': 'не измерено: сбой замера показателей',
    'rep.pulse.unmeasured.gbp_stale': 'не измерено: устаревшие данные',
    'rep.pulse.unmeasured.gbp_account_not_set': 'не измерено: аккаунт Google Business Profile не настроен',
    'rep.pulse.unmeasured.gbp_account_not_connected': 'не измерено: Google-аккаунт не подключён для {email}',
    'rep.pulse.unmeasured.location_not_visible_to_token': 'не измерено: карточка {place_id} не видна владельцу токена {email}',
    'rep.pulse.unmeasured.performance_api_error': 'не измерено: ошибка Google Performance API {status_code}: {message}',
};

export const SLOVAR = Object.freeze({
    en: Object.freeze(EN),
    ru: Object.freeze(RU),
});

/**
 * Определение категории множественного числа:
 *  - ru:
 *      1, 21, 31... (кроме 11) -> 'one'
 *      2..4, 22..24... (кроме 12..14) -> 'few'
 *      5..20, 25..30... -> 'many'
 *  - en:
 *      1 -> 'one'
 *      все остальные (0, 2, 3...) -> 'other' (в нашем словаре мапится на 'many'/'few' для 100% паритета)
 */
export function pluralCategory(lang, n) {
    const num = Math.abs(Number(n) || 0);
    if (lang === 'ru') {
        const rem100 = num % 100;
        const rem10 = num % 10;
        if (rem100 >= 11 && rem100 <= 14) return 'many';
        if (rem10 === 1) return 'one';
        if (rem10 >= 2 && rem10 <= 4) return 'few';
        return 'many';
    }
    return num === 1 ? 'one' : 'many';
}

/**
 * Склонение фразы по базовому ключу.
 * Пример: sklonit('ru', 'rep.plural.obrashcheniy', 5) -> "5 обращений"
 */
export function sklonit(lang, baseKey, count, vars = {}) {
    const cat = pluralCategory(lang, count);
    const key = `${baseKey}.${cat}`;
    return perevesti(lang, key, { ...vars, n: count });
}

/**
 * Перевод ключа. Нет ключа (или языка) → `[key]`: пропуск ВИДЕН на экране.
 * `{var}` подставляется из vars; отсутствующая переменная остаётся `{var}`.
 */
export function perevesti(lang, key, vars) {
    const yazyk = lang === 'ru' ? 'ru' : 'en';
    const slovar = SLOVAR[yazyk];
    if (!slovar || !Object.prototype.hasOwnProperty.call(slovar, key)) {
        return `[${key}]`;
    }
    const shablon = slovar[key];
    if (vars === undefined) return shablon;
    const v = vars && typeof vars === 'object' ? vars : {};
    return shablon.replace(/\{([a-zA-Z_]+)\}/g, (celoe, imya) => (
        Object.prototype.hasOwnProperty.call(v, imya) && v[imya] !== undefined && v[imya] !== null
            ? String(v[imya])
            : '—'
    ));
}

/** Ключи `a`, которых нет в `b` (отсортировано). */
export function raznitsaKlyuchey(a, b) {
    const vB = b || {
};
    return Object.keys(a || {})
        .filter((k) => !Object.prototype.hasOwnProperty.call(vB, k))
        .sort();
}
